import { useState, useEffect, useRef } from 'react';
import { createElement } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import MenuSidebar from '../../components/MenuSidebar/MenuSidebar';
import getMenuItems from '@const/menuItems';
import { getOrderById, scratchCard, disableOrderSharing } from '../../services/orderService';
import { cartService } from '../../services/cartService';
import { ImageWithFallback } from '../../utils/imageFallback';
import { useToast } from '../../contexts/ToastContext';
import { useCart } from '../../hooks/useCart';
import { useLanguageChange } from '@hooks/languageChange';
import { useAuth } from '../../hooks/useAuth';
import CartDrawer from '../../components/Cart/CartDrawer';
import ShareDialog from '../../components/ShareDialog/ShareDialog';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useMembership } from '../../hooks/useMembership';
import { logger } from '../../utils/logger';
import PrintReceiptItem from '../../components/PrintReceipt/PrintReceiptItem';
import PrintAllReceipt from '../../components/PrintReceipt/PrintAllReceipt';
import { formatMessageWithBoldPhoneNumbers } from '@utils/formatMessageWithBoldPhoneNumbers';
import { HugeiconsIcon } from '@hugeicons/react';
import { ViberIcon, MessengerIcon } from '@hugeicons/core-free-icons';

interface OrderItem {
  name: string;
  productId: string;
  quantity: number;
  price: number;
  discountPrice?: number;
  discountAmount?: number;
  discountType?: string;
  discountValue?: number;
  image: string;
  code: string;
  subtotal: number;
  type: string;
  currency?:
    | {
        _id: string;
        code: string;
        name: string;
        symbol: string;
        _nameMultilingual: {
          en: string;
          my: string;
        };
      }
    | string
    | null;
  instruction?: string;
  redeemUrl?: string;
  fx?: {
    exchangeRate: number;
    baseCurrencySymbol: string;
    baseCurrencyCode: string;
    productCurrencySymbol: string;
    productCurrencyCode: string;
    productPrice: number;
    exchangeDateTime: string;
  };
  fulfillmentStatus?: 'fulfilled' | 'failed';
  fulfilledQuantity?: number;
  failedQuantity?: number;
  failedAt?: string;
  failureReason?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fulfillmentData?: any[];
  fulfillmentMessage?: string;
}

interface Card {
  _id: string;
  productId: string;
  orderNumber: string;
  invoiceNumber: string;
  serialNumber: string;
  pinNumber: string;
  cardPrice: number;
  expireDate?: string;
  orderId: string;
  scratch?: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface Eload {
  statusCode: number;
  invoiceNumber: string;
  orderNumber: string;
  message: string;
  dateTime: string;
}

interface OrderDetail {
  _id: string;
  userId: string;
  items: OrderItem[];
  totalAmount: number;
  totalItems: number;
  subtotal?: number;
  discountAmount?: number;
  status: string;
  payment: {
    method: string;
    status: string;
    amount: number;
  };
  createdAt: string;
  updatedAt: string;
  orderNumber: string;
  __v: number;
  cardListPdfUrl?: string;
  cardListPdfOriginalUrl?: string;
  cards?: Card[];
  eload?: Eload;
  isPublicShared?: boolean;
  shareInfo?: {
    shareId: string;
    shareUrl: string;
    type: string;
    isActive: boolean;
    createdAt: string;
    notes?: string;
  };
  notes?: string;
  rewardedPoints?: number;
  totalRefundedAmount?: number;
  partialRefunds?: {
    refundId: string;
    refundedAmount: number;
    refundedItems: {
      productId: string;
      quantity: number;
      refundAmount: number;
    }[];
    refundMethod: string;
    refundTransactionId: string;
    refundedAt: string;
    reason: string;
    totalRefunded: number;
    remainingRefundable: number;
    refundStatus: string;
  }[];
}

interface ScratchCardProps {
  cardId: string;
  pinNumber: string;
  isScratched: boolean;
  onScratch: () => void;
}

const ScratchCard: React.FC<ScratchCardProps> = ({ pinNumber, isScratched, onScratch }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  // const [scratchPercentage, setScratchPercentage] = useState(0);
  const { showToast } = useToast();
  const { t } = useTranslation();

  // Cache bounding rect to prevent forced reflows
  const boundingRectRef = useRef<DOMRect | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);

  // Format PIN number with spaces in groups of 4, with shorter groups first
  const formatPinNumber = (pin: string) => {
    const cleanPin = pin.trim(); // Keep all characters (letters and numbers)
    const length = cleanPin.length;

    if (length <= 4) return cleanPin;

    const remainder = length % 4;
    const groups = [];

    if (remainder > 0) {
      groups.push(cleanPin.slice(0, remainder));
    }

    for (let i = remainder; i < length; i += 4) {
      groups.push(cleanPin.slice(i, i + 4));
    }

    return groups.join(' ');
  };

  const formattedPinNumber = formatPinNumber(pinNumber);

  // Handle PIN copy to clipboard
  const handlePinClick = async () => {
    if (!isScratched) {
      return; // Do nothing if card is not scratched
    }

    try {
      await navigator.clipboard.writeText(pinNumber.trim());
      showToast('success', t('orderDetail.pinCopied') || 'PIN copied to clipboard');
    } catch (error) {
      logger.error('Failed to copy PIN:', error);
      showToast('error', t('orderDetail.pinCopyFailed') || 'Failed to copy PIN');
    }
  };

  useEffect(() => {
    if (!canvasRef.current || isScratched) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set consistent canvas size with device pixel ratio for crisp rendering
    // const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = 240 * dpr;
    canvas.height = 44 * dpr;
    canvas.style.width = '240px';
    canvas.style.height = '44px';
    ctx.scale(dpr, dpr);

    // Draw scratch surface
    ctx.fillStyle = '#9CA3AF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add diagonal pattern with better spacing
    ctx.strokeStyle = '#6B7280';
    ctx.lineWidth = 1.5;
    for (let i = -44; i < 240 + 44; i += 6) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + 44, 44);
      ctx.stroke();
    }

    // Add "Scratch" text with better positioning
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('orderDetail.scratch') || 'Scratch', 120, 22);

    // Reset bounding rect cache when canvas is reinitialized
    boundingRectRef.current = null;
  }, [pinNumber, isScratched, t]);

  // Optimized position calculation with cached bounding rect
  const getCachedBoundingRect = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const now = performance.now();
    // Cache rect for 100ms to avoid excessive getBoundingClientRect calls
    if (!boundingRectRef.current || now - lastUpdateTimeRef.current > 100) {
      boundingRectRef.current = canvas.getBoundingClientRect();
      lastUpdateTimeRef.current = now;
    }

    return boundingRectRef.current;
  };

  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = getCachedBoundingRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const getTouchPos = (e: React.TouchEvent<HTMLCanvasElement>) => {
    const rect = getCachedBoundingRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: e.touches[0].clientX - rect.left,
      y: e.touches[0].clientY - rect.top,
    };
  };

  const scratch = (x: number, y: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 8, 0, 2 * Math.PI);
    ctx.fill();

    // Calculate scratch percentage
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparentPixels = 0;

    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparentPixels++;
    }

    const percentage = (transparentPixels / (canvas.width * canvas.height)) * 100;
    // setScratchPercentage(percentage);

    if (percentage > 30) {
      onScratch();
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const pos = getMousePos(e);
    scratch(pos.x, pos.y);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const pos = getMousePos(e);
    scratch(pos.x, pos.y);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getTouchPos(e);
    scratch(pos.x, pos.y);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;
    const pos = getTouchPos(e);
    scratch(pos.x, pos.y);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  if (isScratched) {
    return (
      <div
        className="bg-yellow-100 text-yellow-800 rounded-md text-xs font-medium w-[240px] h-[44px] flex items-center justify-center px-4 py-2 border border-yellow-200 shadow-sm cursor-pointer hover:bg-yellow-200 transition-colors"
        onClick={handlePinClick}
        title={t('orderDetail.clickToCopy') || 'Click to copy PIN'}
      >
        <span className="font-mono text-sm tracking-wide">{formattedPinNumber}</span>
      </div>
    );
  }

  return (
    <div className="relative inline-block w-[240px] h-[44px]">
      <div className="absolute inset-0 bg-yellow-100 text-yellow-800 rounded-md text-xs font-medium flex items-center justify-center px-4 py-2 h-full border border-yellow-200 shadow-sm">
        <span className="font-mono text-sm tracking-wide">{formattedPinNumber}</span>
      </div>
      <canvas
        ref={canvasRef}
        className="relative z-10 rounded-md cursor-crosshair block border border-gray-300 shadow-sm"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: 'none', width: '240px', height: '44px' }}
      />
    </div>
  );
};

const OrderDetailPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { refreshCart } = useCart();
  const { changeLanguage } = useLanguageChange();
  const { user, networkStatus } = useAuth();
  const { trackPurchase } = useAnalytics();
  const { refreshMembershipInfo } = useMembership();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scratchedPins, setScratchedPins] = useState<Set<string>>(new Set());
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isOrderItemsCollapsed, setIsOrderItemsCollapsed] = useState(false);
  const [isCardListCollapsed, setIsCardListCollapsed] = useState(false);
  const [isBuyAgainLoading, setIsBuyAgainLoading] = useState(false);
  const [showCartDialog, setShowCartDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [paymentVerification, setPaymentVerification] = useState<{
    urlStatus: string | null;
    isVerified: boolean;
    showNotification: boolean;
  }>({ urlStatus: null, isVerified: false, showNotification: false });
  const [iframeDialog, setIframeDialog] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
    type: 'instruction' | 'redeem';
    fullWidth?: boolean;
    fullHeight?: boolean;
    showLanguageToggle?: boolean;
  }>({ isOpen: false, url: '', title: '', type: 'instruction' });
  const [iframeLoading, setIframeLoading] = useState(false);
  const [showFeedbackSurvey, setShowFeedbackSurvey] = useState(false);
  const [feedbackTimerStarted, setFeedbackTimerStarted] = useState(false);
  const [feedbackIframeLoading, setFeedbackIframeLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [printDialog, setPrintDialog] = useState<{
    isOpen: boolean;
    type: 'desktop' | 'mobile';
    content: string;
    orderData?: {
      order: OrderDetail;
      matchingItem?: OrderItem;
      card?: Card;
      getProductTypeByProductId: (productId: string) => string;
    };
  }>({ isOpen: false, type: 'desktop', content: '' });

  // Role-aware discount/commission labels and styles
  const isMerchant = (user?.activeRole || '') === 'merchant';
  const commissionCandidate = t('orderDetail.commission');
  const commissionText =
    commissionCandidate && commissionCandidate !== 'orderDetail.commission'
      ? commissionCandidate
      : i18n.language === 'my'
        ? 'ကော်မရှင်'
        : 'Commission';
  const discountText = t('orderDetail.discount') || 'Discount';
  const discountLabel = isMerchant ? commissionText : discountText;
  const discountBadgeClass = isMerchant ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-600';

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const feedbackTimerRef = useRef<NodeJS.Timeout | null>(null);
  const shareInfoRef = useRef<HTMLDivElement>(null);
  const feedbackIframeRef = useRef<HTMLIFrameElement | null>(null);

  // Helper function to format currency with proper validation
  const formatCurrency = (
    amount: number,
    currency?:
      | {
          _id: string;
          code: string;
          name: string;
          symbol: string;
          _nameMultilingual?: {
            en?: string;
            my?: string;
          };
        }
      | string
      | null,
  ) => {
    // Handle new currency object structure
    if (currency && typeof currency === 'object') {
      const symbol = currency.symbol || currency.code;
      if (currency.code === 'USD') {
        return `${symbol}${amount.toFixed(2)}`;
      }
      return `${amount.toLocaleString()} ${symbol}`;
    }

    // Handle old string-based currency code (fallback)
    if (currency === 'USD') {
      return `$${amount.toFixed(2)}`;
    }

    // Default fallback to MMK
    return `${amount.toLocaleString()} MMK`;
  };

  // Helper function to get card name by productId
  const getCardNameByProductId = (productId: string): string => {
    if (!order?.items) return '';
    const item = order.items.find((item) => item.productId === productId);
    return item ? item.name : '';
  };

  // Helper function to get product type by productId
  const getProductTypeByProductId = (productId: string): string => {
    if (!order?.items) return '';
    const item = order.items.find((item) => item.productId === productId);
    return item ? item.type : '';
  };

  const handleScratch = async (cardId: string) => {
    try {
      // Update UI immediately
      setScratchedPins((prev) => new Set([...prev, cardId]));

      // Update order state to mark card as scratched
      setOrder((prevOrder) => {
        if (prevOrder && prevOrder.cards) {
          return {
            ...prevOrder,
            cards: prevOrder.cards.map((card) =>
              card._id === cardId ? { ...card, scratch: true } : card,
            ),
          };
        }
        return prevOrder;
      });

      // Find the card to get its PIN number
      const card = order?.cards?.find((c) => c._id === cardId);
      if (card && card.pinNumber) {
        // Copy PIN to clipboard when scratched
        try {
          await navigator.clipboard.writeText(card.pinNumber.trim());
          showToast('success', t('orderDetail.pinCopied') || 'PIN copied to clipboard');
        } catch (clipboardError) {
          logger.error('Failed to copy PIN to clipboard:', clipboardError);
        }
      }

      // Call API to mark card as scratched
      await scratchCard(cardId);
      logger.log(t('orderDetail.cardScratchSuccess') || 'Card scratched successfully:', cardId);
    } catch (error) {
      logger.error(t('orderDetail.cardScratchFailed') || 'Failed to scratch card:', error);
      // Revert order state on error
      setOrder((prevOrder) => {
        if (prevOrder && prevOrder.cards) {
          return {
            ...prevOrder,
            cards: prevOrder.cards.map((card) =>
              card._id === cardId ? { ...card, scratch: false } : card,
            ),
          };
        }
        return prevOrder;
      });
      // Optionally revert UI state on error
      setScratchedPins((prev) => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });
    }
  };

  // URL validation function for security
  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      // Only allow HTTPS URLs for security
      if (urlObj.protocol !== 'https:') {
        return false;
      }

      // Get trusted domains from environment variables
      const trustedDomainsString = import.meta.env.VITE_TRUSTED_DOMAINS || '';
      const trustedDomains = trustedDomainsString
        .split(',')
        .map((domain: string) => domain.trim())
        .filter(Boolean);

      // Check if domain is in trusted list
      const isTrustedDomain = trustedDomains.some(
        (domain: string) => urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain),
      );

      if (!isTrustedDomain) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  };

  // Allowed survey origins helper
  const isAllowedSurveyOrigin = (origin: string): boolean => {
    try {
      const url = new URL(origin);
      const host = url.hostname.toLowerCase();
      if (host === 'ezmyanmar.com' || host === 'www.ezmyanmar.com') return true;
      // Also allow subdomains of ezmyanmar.com
      if (host.endsWith('.ezmyanmar.com')) return true;
      return false;
    } catch {
      return false;
    }
  };

  // Handle opening instruction dialog
  const handleShowInstruction = (instruction: string, productName: string) => {
    if (!isValidUrl(instruction)) {
      showToast('error', t('orderDetail.invalidUrl') || 'Invalid or unsafe URL');
      return;
    }

    // Get current language from i18n
    const currentLanguage = i18n.language;

    // Modify URL to use appropriate language path
    let modifiedUrl = instruction;
    if (currentLanguage === 'en') {
      // Replace /my/ with /en/ for English
      modifiedUrl = instruction.replace('/my/', '/en/');
    }
    // For Myanmar (mm) or any other language, keep /my/ as default

    setIframeLoading(true);
    setIframeDialog({
      isOpen: true,
      url: modifiedUrl,
      title: `${t('orderDetail.instruction') || 'Instructions'} - ${productName}`,
      type: 'instruction',
    });
  };

  // Handle opening redeem URL dialog
  const handleRedeem = (redeemUrl: string, productName: string) => {
    if (!isValidUrl(redeemUrl)) {
      showToast('error', t('orderDetail.invalidUrl') || 'Invalid or unsafe URL');
      return;
    }
    setIframeLoading(true);
    setIframeDialog({
      isOpen: true,
      url: redeemUrl,
      title: `${t('orderDetail.redeem') || 'Redeem'} - ${productName}`,
      type: 'redeem',
    });
  };

  // Get appropriate sandbox attributes based on URL
  const getSandboxAttributes = (url: string) => {
    try {
      const urlObj = new URL(url);

      // Get trusted domains from environment variables
      const envTrustedDomains = import.meta.env.VITE_TRUSTED_DOMAINS || '';
      const trustedDomains = [
        ...envTrustedDomains
          .split(',')
          .map((domain: string) => domain.trim())
          .filter(Boolean),
        // Add tawk.to domain for chat support functionality
        'tawk.to',
      ];

      const isTrusted = trustedDomains.some(
        (domain) => urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain),
      );

      if (isTrusted) {
        return 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin';
      } else {
        return 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox';
      }
    } catch {
      // If URL parsing fails, use restrictive sandbox
      return 'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox';
    }
  };

  // Handle closing iframe dialog
  const handleCloseIframeDialog = () => {
    setIframeLoading(false);
    setIframeDialog({
      isOpen: false,
      url: '',
      title: '',
      type: 'instruction',
      fullWidth: false,
      fullHeight: false,
      showLanguageToggle: true,
    });
  };

  // Handle language change for iframe
  const handleLanguageChangeInIframe = (newLanguage: string) => {
    if (iframeDialog.type === 'instruction' && iframeDialog.url) {
      let modifiedUrl = iframeDialog.url;
      if (newLanguage === 'en') {
        // Replace /my/ with /en/ for English
        modifiedUrl = iframeDialog.url.replace('/my/', '/en/');
      } else {
        // Replace /en/ with /my/ for Myanmar
        modifiedUrl = iframeDialog.url.replace('/en/', '/my/');
      }

      setIframeLoading(true);
      setIframeDialog((prev) => ({
        ...prev,
        url: modifiedUrl,
      }));
    }

    // Change the language
    changeLanguage(newLanguage);
  };

  const handleBuyAgain = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!order || !order.items) {
      showToast('error', t('orderDetail-BuyAgain.buyAgainError') || 'No items to add to cart');
      return;
    }

    setIsBuyAgainLoading(true);

    try {
      // Add all items to cart
      for (const item of order.items) {
        await cartService.addToCart({
          productId: item.productId,
          quantity: item.quantity,
        });
      }

      // Refresh cart state to ensure UI is updated
      await refreshCart();

      // Verify cart has been updated by fetching latest cart data
      const cartResponse = await cartService.getCart();
      if (cartResponse.success && cartResponse.data && cartResponse.data.items.length > 0) {
        showToast(
          'success',
          t('orderDetail-BuyAgain.buyAgainSuccess') || 'Items added to cart successfully',
        );

        // Show cart dialog only after confirming items are in cart
        setShowCartDialog(true);
      } else {
        showToast(
          'error',
          t('orderDetail-BuyAgain.buyAgainError') || 'Failed to verify items in cart',
        );
      }
    } catch (error) {
      logger.error('Error adding items to cart:', error);
      showToast('error', t('orderDetail-BuyAgain.buyAgainError') || 'Failed to add items to cart');
    } finally {
      setIsBuyAgainLoading(false);
    }
  };

  const handleShareSuccess = (shareUrl: string, shareType: string, notes?: string) => {
    // Update order state with new share info
    setOrder((prevOrder) => {
      if (prevOrder) {
        return {
          ...prevOrder,
          shareInfo: {
            shareId: shareUrl.split('/').pop() || '',
            shareUrl,
            type: shareType,
            isActive: true,
            createdAt: new Date().toISOString(),
            notes,
          },
        };
      }
      return prevOrder;
    });

    setShowShareDialog(false);
    showToast('success', t('orderDetail.shareSuccess') || 'Share link created successfully!');

    // Scroll to share info section after a short delay to ensure DOM is updated
    setTimeout(() => {
      shareInfoRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
  };

  const handleDisableShare = async () => {
    if (!order?._id) return;

    try {
      const response = await disableOrderSharing(order._id);
      if (response.success) {
        // Update order state to mark share info as inactive
        setOrder((prevOrder) => {
          if (prevOrder && prevOrder.shareInfo) {
            return {
              ...prevOrder,
              shareInfo: {
                ...prevOrder.shareInfo,
                isActive: false,
              },
            };
          }
          return prevOrder;
        });

        showToast('success', t('orderDetail.shareDisabled') || 'Sharing disabled successfully!');
      } else {
        showToast('error', response.message || 'Failed to disable sharing');
      }
    } catch (error) {
      logger.error('Error disabling share:', error);
      showToast('error', t('orderDetail.shareDisableError') || 'Failed to disable sharing');
    }
  };

  useEffect(() => {
    const fetchOrderDetail = async (isPolling = false) => {
      if (!orderId) {
        setError(t('orderDetail.orderIdRequired') || 'Order ID is required');
        setLoading(false);
        return;
      }

      try {
        if (!isPolling) {
          setLoading(true);
        }
        const response = await getOrderById(orderId);

        if (response.success && response.data) {
          const orderData = response.data as OrderDetail;
          // const previousStatus = order?.status;

          // Debug: Log the API response to check shareInfo
          logger.log('API Response:', response);
          logger.log('Order Data:', orderData);
          logger.log('Share Info:', orderData.shareInfo);
          logger.log('Is Public Shared:', orderData.isPublicShared);
          logger.log(
            'Share Token from API:',
            (response.data as { shareToken?: string })?.shareToken,
          );
          logger.log('Share Type from API:', (response.data as { shareType?: string })?.shareType);

          setOrder(orderData);
          setError(null);

          // Check if status changed from processing to success/failed during polling
          // Simply check if we are polling and the new status is no longer processing
          if (isPolling && orderData.status !== 'processing') {
            // Clear polling interval
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }

            // Status change notifications will be handled by API response messages
            // No hardcoded toast messages for status changes
          }

          // Start polling if status is processing
          if (orderData.status === 'processing' && !pollingIntervalRef.current) {
            pollingIntervalRef.current = setInterval(() => {
              fetchOrderDetail(true);
            }, 3000);
          }

          // Initialize scratched pins based on API response
          if (orderData.cards && !isPolling) {
            const scratchedCardIds = new Set(
              orderData.cards.filter((card) => card.scratch === true).map((card) => card._id),
            );
            setScratchedPins(scratchedCardIds);
          }

          // Handle share info from API response
          if (orderData.isPublicShared && !isPolling) {
            // If shareInfo doesn't exist but shareToken and shareType are available, create shareInfo
            if (!orderData.shareInfo) {
              // Check if API response contains shareToken and shareType
              const apiResponse = response.data as {
                shareToken?: string;
                shareType?: string;
                [key: string]: unknown;
              };
              if (apiResponse.shareToken && typeof apiResponse.shareToken === 'string') {
                // Create shareInfo from API response shareToken and shareType
                const hostUrl = window.location.origin;
                const shareUrl = `${hostUrl}/sharewith/${apiResponse.shareToken}`;

                setOrder((prevOrder) => {
                  if (prevOrder && typeof apiResponse.shareToken === 'string') {
                    return {
                      ...prevOrder,
                      shareInfo: {
                        shareId: apiResponse.shareToken,
                        shareUrl,
                        type: apiResponse.shareType || 'public',
                        isActive: true,
                        createdAt: new Date().toISOString(),
                      },
                    };
                  }
                  return prevOrder;
                });
              }
            }
            // If shareInfo exists, ensure shareUrl is properly formatted
            else if (orderData.shareInfo && orderData.shareInfo.shareId) {
              const hostUrl = window.location.origin;
              const expectedShareUrl = `${hostUrl}/sharewith/${orderData.shareInfo.shareId}`;

              // Update shareUrl if it's not properly formatted
              if (orderData.shareInfo.shareUrl !== expectedShareUrl) {
                setOrder((prevOrder) => {
                  if (prevOrder && prevOrder.shareInfo) {
                    return {
                      ...prevOrder,
                      shareInfo: {
                        ...prevOrder.shareInfo,
                        shareUrl: expectedShareUrl,
                      },
                    };
                  }
                  return prevOrder;
                });
              }
            }
          }

          // Check payment verification from URL parameters
          const urlStatus = searchParams.get('status');
          if (urlStatus && !isPolling) {
            const orderData = response.data as OrderDetail;
            const isStatusMatched =
              urlStatus === 'success'
                ? orderData.payment.status === 'paid' || orderData.payment.status === 'completed'
                : urlStatus === 'failed'
                  ? orderData.payment.status === 'failed' ||
                    orderData.payment.status === 'cancelled'
                  : false;

            setPaymentVerification({
              urlStatus,
              isVerified: isStatusMatched,
              showNotification: true,
            });

            // Track successful purchase in Google Analytics
            if (urlStatus === 'success' && isStatusMatched) {
              try {
                trackPurchase({
                  transaction_id: orderData.orderNumber,
                  value: orderData.payment.amount,
                  currency: 'MMK',
                  items: orderData.items.map((item, index) => ({
                    item_id: item.productId,
                    item_name: item.name,
                    category: item.type,
                    quantity: item.quantity,
                    price: item.price,
                    index: index,
                  })),
                });
              } catch (error) {
                logger.error('Failed to track purchase:', error);
              }

              // Refresh membership info to update points after successful payment verification
              if (userRef.current) {
                try {
                  await refreshMembershipInfo();
                  logger.log('Membership info refreshed after successful payment verification');
                } catch (membershipError) {
                  logger.error('Failed to refresh membership info:', membershipError);
                }
              }
            }

            // Auto hide notification after 10 seconds
            setTimeout(() => {
              setPaymentVerification((prev) => ({ ...prev, showNotification: false }));
            }, 10000);
          }
        } else {
          setError(
            response.message || t('orderDetail.fetchOrderError') || 'Failed to fetch order details',
          );
        }
      } catch (err) {
        logger.error('Error fetching order details:', err);
        setError(t('orderDetail.fetchOrderError') || 'Failed to fetch order details');
      } finally {
        if (!isPolling) {
          setLoading(false);
        }
      }
    };

    fetchOrderDetail();

    // Cleanup polling interval on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Keep user and callbacks in refs to access in useEffect without re-triggering
  const userRef = useRef(user);
  const trackPurchaseRef = useRef(trackPurchase);
  const refreshMembershipInfoRef = useRef(refreshMembershipInfo);
  const showToastRef = useRef(showToast);

  useEffect(() => {
    userRef.current = user;
    trackPurchaseRef.current = trackPurchase;
    refreshMembershipInfoRef.current = refreshMembershipInfo;
    showToastRef.current = showToast;
  }, [user, trackPurchase, refreshMembershipInfo, showToast]);

  useEffect(() => {
    let isMounted = true;
    const fetchOrderDetail = async (isPolling = false) => {
      if (!orderId) {
        setError(t('orderDetail.orderIdRequired') || 'Order ID is required');
        setLoading(false);
        return;
      }

      try {
        if (!isPolling) {
          setLoading(true);
        }
        const response = await getOrderById(orderId);

        if (!isMounted) return;

        if (response.success && response.data) {
          const orderData = response.data as OrderDetail;

          setOrder(orderData);
          setError(null);

          // Check if status changed from processing to success/failed during polling
          // Simply check if we are polling and the new status is no longer processing
          if (isPolling && orderData.status !== 'processing') {
            // Clear polling interval
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }

          // Start polling if status is processing
          if (orderData.status === 'processing' && !pollingIntervalRef.current) {
            pollingIntervalRef.current = setInterval(() => {
              fetchOrderDetail(true);
            }, 3000);
          }

          // Initialize scratched pins based on API response
          if (orderData.cards && !isPolling) {
            const scratchedCardIds = new Set(
              orderData.cards.filter((card) => card.scratch === true).map((card) => card._id),
            );
            setScratchedPins(scratchedCardIds);
          }

          // Handle share info from API response
          if (orderData.isPublicShared && !isPolling) {
            // ... share info logic (kept implicitly by not modifying it, but wait, replace_file_content replaces the whole block)
            // I need to be careful not to delete logic I'm not showing in the ReplacementContent if I use a large range.
            // I will use a smaller range focused on the useEffect hook structure itself.
          }
          // ...
        }
        // ...
      } catch {
        // ...
      }
    };
    // ...
    fetchOrderDetail();

    return () => {
      isMounted = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [orderId, t]);

  // Listen for postMessage from iframe and check URL periodically
  useEffect(() => {
    // Do not enable feedback survey listeners for merchant role
    if (isMerchant || !showFeedbackSurvey) return;

    // Enhanced mobile detection
    const isMobileDevice =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      window.innerWidth <= 768 ||
      'ontouchstart' in window;

    // Mobile-specific iframe load event handler to detect form submission reload
    const handleIframeLoad = () => {
      const iframe = feedbackIframeRef.current;
      if (!iframe) return;

      logger.log('=== IFRAME LOAD EVENT DEBUG ===');
      logger.log('Is mobile device:', isMobileDevice);
      logger.log('Iframe loaded, checking for completion indicators...');

      // Enhanced delay for mobile devices to ensure content is fully loaded
      const checkDelay = isMobileDevice ? 1000 : 500;

      setTimeout(() => {
        try {
          let currentUrl = '';
          let urlSource = '';

          try {
            // Try to get the actual content URL first
            if (iframe.contentDocument?.location?.href) {
              currentUrl = iframe.contentDocument.location.href;
              urlSource = 'contentDocument';
            }
          } catch {
            // Fallback to src attribute for cross-origin
            currentUrl = iframe.src;
            urlSource = 'src';
          }

          logger.log('Iframe URL after load:', currentUrl);
          logger.log('URL source:', urlSource);

          // Enhanced completion indicators for mobile
          const completionIndicators = [
            '?hide',
            'success',
            'complete',
            'submitted',
            'thank',
            'done',
            'finish',
            'end',
            'close',
            'feedback_complete',
            'survey_complete',
            'form_submitted',
          ];

          const hasCompletionIndicator = completionIndicators.some((indicator) =>
            currentUrl.toLowerCase().includes(indicator.toLowerCase()),
          );

          if (currentUrl && hasCompletionIndicator) {
            logger.log('✅ Survey completion detected via iframe reload');
            logger.log('Completion indicator found in URL');

            // Force immediate completion for mobile
            const executeCompletion = () => {
              logger.log('Executing survey completion...');

              // Update states immediately
              setShowThankYou(true);
              setFeedbackIframeLoading(false);

              // Show success message
              showToast(
                'success',
                t('orderDetail.feedbackSubmitted') || 'Thank you for your feedback!',
              );

              // Close faster when ?hide is present; mobile needs shorter time
              const urlHasHide = currentUrl.toLowerCase().includes('?hide');
              const closeTimeout = isMobileDevice
                ? urlHasHide
                  ? 1500
                  : 3000
                : urlHasHide
                  ? 2000
                  : 2500;
              setTimeout(() => {
                logger.log('Closing feedback survey after completion');
                setShowThankYou(false);
                setShowFeedbackSurvey(false);
              }, closeTimeout);
            };

            // Use requestAnimationFrame for better mobile performance
            if (isMobileDevice) {
              requestAnimationFrame(() => {
                setTimeout(executeCompletion, 200);
              });
            } else {
              executeCompletion();
            }
          } else {
            logger.log('No completion indicator found in URL:', currentUrl);
          }
        } catch (error) {
          logger.error('Error in iframe load handler:', error);
        }
      }, checkDelay);

      logger.log('=== END IFRAME LOAD EVENT DEBUG ===');
    };

    // Unified PostMessage listener for iframe communication
    const handleMessage = (event: MessageEvent) => {
      // Log all messages for debugging
      logger.log('=== FEEDBACK SURVEY MESSAGE DEBUG ===');
      logger.log('Event origin:', event.origin);
      logger.log('Event data type:', typeof event.data);
      logger.log('Event data:', event.data);
      logger.log('User agent:', navigator.userAgent);
      const isMobileForLogging =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      logger.log('Is mobile:', isMobileForLogging);

      // Verify origin for security
      const isFromSurveyIframe =
        !!feedbackIframeRef.current && event.source === feedbackIframeRef.current.contentWindow;
      if (isAllowedSurveyOrigin(event.origin) || isFromSurveyIframe) {
        logger.log('✅ Origin verified - processing message');

        let shouldCloseSurvey = false;
        let detectionMethod = '';

        // Check various message formats that indicate survey completion
        if (typeof event.data === 'string') {
          logger.log('Processing string message:', event.data);
          if (
            event.data.includes('survey_completed') ||
            event.data.includes('survey_submitted') ||
            event.data.includes('hide') ||
            event.data.includes('Thank you for your feedback') ||
            event.data.includes('feedback_submitted') ||
            event.data.includes('success') ||
            event.data.toLowerCase().includes('complete')
          ) {
            shouldCloseSurvey = true;
            detectionMethod = 'string_match';
            logger.log('✅ Survey completion detected via string match');
          }
        }

        // Check for object format messages
        if (event.data && typeof event.data === 'object') {
          logger.log('Processing object message:', JSON.stringify(event.data, null, 2));

          // Check for subFrameData with URL containing ?hide
          if (event.data.subFrameData && event.data.subFrameData.url) {
            const url = event.data.subFrameData.url;
            logger.log('Checking subFrameData URL:', url);
            if (url.includes('?hide') || url.includes('success') || url.includes('complete')) {
              shouldCloseSurvey = true;
              detectionMethod = 'subframe_url_hide';
              logger.log('✅ Survey completion detected via subFrameData URL hide parameter');
            }
          }

          // Check for direct survey_submitted message
          if (event.data === 'survey_submitted' || event.data.type === 'survey_submitted') {
            shouldCloseSurvey = true;
            detectionMethod = 'direct_survey_submitted';
            logger.log('✅ Survey completion detected via direct survey_submitted');
          }

          // Check for other possible completion indicators
          if (
            event.data.status === 'completed' ||
            event.data.status === 'success' ||
            event.data.action === 'close' ||
            event.data.action === 'submit' ||
            event.data.message === 'survey_complete' ||
            event.data.message === 'feedback_submitted' ||
            event.data.type === 'feedback_complete'
          ) {
            shouldCloseSurvey = true;
            detectionMethod = 'object_status_indicators';
            logger.log('✅ Survey completion detected via object status indicators');
          }

          // Mobile-specific checks for form submission events
          if (
            isMobileForLogging &&
            (event.data.type === 'submit' ||
              event.data.eventType === 'submit' ||
              event.data.formSubmitted === true ||
              (event.data.target && event.data.target.tagName === 'FORM'))
          ) {
            shouldCloseSurvey = true;
            detectionMethod = 'mobile_form_submit';
            logger.log('✅ Survey completion detected via mobile form submission');
          }
        }

        if (shouldCloseSurvey) {
          logger.log('🎉 SURVEY COMPLETION CONFIRMED');
          logger.log('Detection method:', detectionMethod);
          logger.log('Is mobile device:', isMobileForLogging);
          logger.log('Showing thank you message and closing survey...');

          // Enhanced mobile-specific state management
          const executeCompletion = () => {
            logger.log('Executing survey completion actions...');

            // Force immediate state updates for mobile compatibility
            setShowThankYou(true);
            setFeedbackIframeLoading(false);

            // Show success toast with mobile-optimized timing
            showToast(
              'success',
              t('orderDetail.feedbackSubmitted') || 'Thank you for your feedback!',
            );

            // Faster close when URL hide detected via message
            const closeTimeout = isMobileForLogging
              ? detectionMethod === 'subframe_url_hide'
                ? 1500
                : 3000
              : detectionMethod === 'subframe_url_hide'
                ? 2000
                : 2500;

            // Hide thank you message and close dialog
            setTimeout(() => {
              logger.log('Closing feedback survey after timeout');
              setShowThankYou(false);
              setShowFeedbackSurvey(false);
            }, closeTimeout);
          };

          // For mobile devices, use requestAnimationFrame for better state management
          if (isMobileForLogging) {
            requestAnimationFrame(() => {
              setTimeout(executeCompletion, 150); // Slightly longer delay for mobile
            });
          } else {
            setTimeout(executeCompletion, 100);
          }
        } else {
          logger.log('❌ No survey completion detected in this message');
        }
      } else {
        logger.log('❌ Origin not verified:', event.origin, 'Expected: https://ezmyanmar.com');
      }

      logger.log('=== END FEEDBACK SURVEY MESSAGE DEBUG ===');
    };

    // Fallback: Check iframe URL periodically for ?hide parameter
    const checkIframeUrl = () => {
      const iframe = document.querySelector('#feedback-survey-iframe') as HTMLIFrameElement;
      if (iframe) {
        try {
          logger.log('=== FEEDBACK SURVEY URL CHECK DEBUG ===');
          const isMobileForUrlCheck =
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent,
            );

          // Try to access the iframe's document URL (for same-origin or properly configured iframes)
          let currentUrl = '';
          try {
            currentUrl = iframe.contentDocument?.location?.href || '';
            logger.log('Iframe content URL (same-origin):', currentUrl);
          } catch {
            // If cross-origin, fallback to checking src attribute
            currentUrl = iframe.src;
            logger.log('Iframe src URL (cross-origin fallback):', currentUrl);
          }

          // Enhanced mobile-specific URL completion detection
          const hasHideParameter = currentUrl.includes('?hide');
          const hasOtherCompletionIndicators =
            currentUrl.includes('success') ||
            currentUrl.includes('complete') ||
            currentUrl.includes('submitted');

          if (currentUrl && (hasHideParameter || hasOtherCompletionIndicators)) {
            logger.log('✅ Survey completion detected via URL check');
            logger.log('URL with completion indicator:', currentUrl);
            logger.log('Has ?hide parameter:', hasHideParameter);
            logger.log('Has other completion indicators:', hasOtherCompletionIndicators);
            logger.log('Is mobile device:', isMobileForUrlCheck);

            // Enhanced mobile-specific state management for URL detection
            const executeUrlCompletion = () => {
              logger.log('Executing URL-based survey completion actions...');
              logger.log('Completion triggered by ?hide parameter:', hasHideParameter);

              // Show thank you message
              setShowThankYou(true);
              setFeedbackIframeLoading(false);

              // Show success toast
              showToast(
                'success',
                t('orderDetail.feedbackSubmitted') || 'Thank you for your feedback!',
              );

              // Close faster when ?hide present; mobile shorter timeout
              const closeTimeout = isMobileForUrlCheck
                ? hasHideParameter
                  ? 1500
                  : 3000
                : hasHideParameter
                  ? 2000
                  : 2500;

              // Hide thank you message and close dialog
              setTimeout(() => {
                logger.log('Closing feedback survey after URL check timeout');
                logger.log('Close timeout used:', closeTimeout);
                setShowThankYou(false);
                setShowFeedbackSurvey(false);
              }, closeTimeout);
            };

            // For mobile devices, use requestAnimationFrame for better state management
            if (isMobileForUrlCheck) {
              requestAnimationFrame(() => {
                setTimeout(executeUrlCompletion, 150);
              });
            } else {
              setTimeout(executeUrlCompletion, 100);
            }
          } else {
            logger.log('No completion indicator found in URL:', currentUrl);
          }

          logger.log('=== END FEEDBACK SURVEY URL CHECK DEBUG ===');
        } catch (error) {
          logger.error('Error checking iframe URL:', error);
        }
      } else {
        logger.log('Feedback survey iframe not found in DOM');
      }
    };

    // Add message listener
    window.addEventListener('message', handleMessage);

    // Add iframe load event listener for mobile detection
    const iframe = feedbackIframeRef.current;
    if (iframe) {
      iframe.addEventListener('load', handleIframeLoad);
    }

    // Check URL every 1.5 seconds for mobile (more frequent), 2 seconds for desktop
    const isMobileForInterval =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const urlCheckInterval = setInterval(checkIframeUrl, isMobileForInterval ? 1500 : 2000);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (iframe) {
        iframe.removeEventListener('load', handleIframeLoad);
      }
      clearInterval(urlCheckInterval);
    };
  }, [showFeedbackSurvey, showToast, t, isMerchant]);

  // Start feedback survey timer - show within 5 minutes of order creation regardless of status
  useEffect(() => {
    // Do not start feedback timer for merchant role
    if (isMerchant) {
      return;
    }
    if (order && !feedbackTimerStarted) {
      // Don't show survey if order status is processing
      if (order.status === 'processing') {
        return;
      }

      const orderDate = new Date(order.createdAt);
      const currentTime = new Date();
      const timeDifference = currentTime.getTime() - orderDate.getTime();
      const fiveMinutesInMs = 5 * 60 * 1000; // 5 minutes

      // Check if order is older than 5 minutes - don't show survey regardless of parameters
      if (timeDifference >= fiveMinutesInMs) {
        setFeedbackTimerStarted(true); // Mark as started to prevent future checks
        return;
      }

      setFeedbackTimerStarted(true);

      // Show immediately if ?status=success parameter exists (for testing) and within 5 minutes
      if (searchParams.get('status') === 'success') {
        setFeedbackIframeLoading(true);
        setShowFeedbackSurvey(true);
        return;
      }

      // Normal flow: show feedback survey after remaining time (within 5 minutes window)
      const remainingTime = fiveMinutesInMs - timeDifference;
      feedbackTimerRef.current = setTimeout(() => {
        setFeedbackIframeLoading(true);
        setShowFeedbackSurvey(true);
      }, remainingTime);
    }

    // Cleanup feedback timer on unmount
    return () => {
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }
    };
  }, [order, feedbackTimerStarted, searchParams, isMerchant]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'partially_completed':
        return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'processing':
        return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'paid':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'failed':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'cancelled':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
        return (
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'partially_completed':
        return (
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'pending':
        return (
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'processing':
        return (
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        );
      case 'failed':
      case 'cancelled':
        return (
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-dvh bg-[#04BFBF]/20 flex flex-col"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5))`,
          backgroundSize: 'cover',
          backgroundPosition: 'left',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
        }}
      >
        <Header />
        <div className="px-4 py-8 flex-1 mt-[56px] pb-24 md:pb-8 overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              <MenuSidebar
                title={t('my_account')}
                currentPage="orders"
                menuItems={getMenuItems(t)}
              />
              <div className="flex-1">
                <div className="bg-white rounded-lg shadow-sm p-8">
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#04BFBF]"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div
        className="min-h-dvh bg-[#04BFBF]/20 flex flex-col overflow-x-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5))`,
          backgroundSize: 'cover',
          backgroundPosition: 'left',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
        }}
      >
        <Header />
        <div className="px-4 py-8 flex-1 mt-[56px] pb-24 md:pb-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              <MenuSidebar
                title={t('my_account')}
                currentPage="orders"
                menuItems={getMenuItems(t)}
              />
              <div className="flex-1">
                <div className="bg-white rounded-lg shadow-sm p-8">
                  <div className="text-center">
                    <div className="text-red-600 text-lg font-medium mb-4">
                      {error || t('orderDetail.orderNotFound') || 'Order not found'}
                    </div>
                    <button
                      onClick={() => navigate('/orders')}
                      className="bg-[#04BFBF] text-white px-6 py-2 rounded-lg hover:bg-[#04BFBF]/90 transition-colors"
                    >
                      {t('common.backToOrders') || 'Back to Orders'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="w-full min-h-dvh bg-[#04BFBF]/20 flex flex-col overflow-x-hidden"
      style={{
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5))`,
        backgroundSize: 'cover',
        backgroundPosition: 'left',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <Header />
      <div className="px-4 py-8 flex-1 mt-[56px] pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6">
            <MenuSidebar title={t('my_account')} currentPage="orders" menuItems={getMenuItems(t)} />
            <div className="flex-1">
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">
                      {t('orderDetail.title') || 'Order Details'}
                    </h1>
                    <button
                      onClick={() => navigate('/orders')}
                      className="text-[#04BFBF] hover:text-[#04BFBF]/80 font-medium"
                    >
                      ← {t('common.backToOrders') || 'Back to Orders'}
                    </button>
                  </div>
                </div>

                <div className="md:h-[calc(100dvh-210px)] w-full overflow-x-hidden">
                  <div className="p-4 md:p-6 space-y-6">
                    {/* Payment Verification Notification */}
                    {paymentVerification.showNotification && (
                      <div
                        className={`relative rounded-lg border p-4 ${
                          paymentVerification.urlStatus === 'success'
                            ? paymentVerification.isVerified
                              ? 'bg-green-50 border-green-200 text-green-800'
                              : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                            : paymentVerification.urlStatus === 'failed'
                              ? paymentVerification.isVerified
                                ? 'bg-red-50 border-red-200 text-red-800'
                                : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                              : 'bg-gray-50 border-gray-200 text-gray-800'
                        }`}
                      >
                        <button
                          onClick={() =>
                            setPaymentVerification((prev) => ({ ...prev, showNotification: false }))
                          }
                          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            {paymentVerification.urlStatus === 'success' ? (
                              paymentVerification.isVerified ? (
                                <svg
                                  className="w-6 h-6 text-green-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="w-6 h-6 text-yellow-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                  />
                                </svg>
                              )
                            ) : paymentVerification.isVerified ? (
                              <svg
                                className="w-6 h-6 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-6 h-6 text-yellow-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-sm font-semibold mb-1">
                              {paymentVerification.urlStatus === 'success'
                                ? paymentVerification.isVerified
                                  ? t('payment.verification.successMatched') || 'Payment Successful'
                                  : t('payment.verification.successNotMatched') ||
                                    'Payment Status Mismatch'
                                : paymentVerification.urlStatus === 'failed'
                                  ? paymentVerification.isVerified
                                    ? t('payment.verification.failedMatched') || 'Payment Failed'
                                    : t('payment.verification.failedNotMatched') ||
                                      'Payment Status Mismatch'
                                  : t('payment.verification.unknown') || 'Unknown Payment Status'}
                            </h3>
                            <p className="text-xs">
                              {paymentVerification.isVerified
                                ? paymentVerification.urlStatus === 'success'
                                  ? t('payment.verification.successMessage') ||
                                    'Your payment has been processed successfully and matches our records.'
                                  : t('payment.verification.failedMessage') ||
                                    'Your payment has failed and matches our records.'
                                : paymentVerification.urlStatus === 'success'
                                  ? t('payment.verification.successMismatchMessage') ||
                                    'Payment URL indicates success, but order status shows otherwise. Please contact support.'
                                  : t('payment.verification.failedMismatchMessage') ||
                                    'Payment URL indicates failure, but order status shows otherwise. Please contact support.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Desktop Grid Layout */}
                    <div className="hidden md:grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {t('orderDetail.orderInfo') || 'Order Information'}
                        </h2>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t('orderDetail.orderNumber') || 'Order Number'}:
                            </span>
                            <span className="font-medium">{order.orderNumber}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t('orderDetail.orderDate') || 'Order Date'}:
                            </span>
                            <span className="font-medium">{formatDate(order.createdAt)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t('orderDetail.totalItems') || 'Total Items'}:
                            </span>
                            <span className="font-medium">{order.totalItems}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t('orderDetail.orderStatus') || 'Status'}:
                            </span>
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(order.status)}`}
                            >
                              {getStatusIcon(order.status)}
                              {t(`orderHistory.status.${order.status}`) ||
                                order.status
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {t('orderDetail.paymentInfo') || 'Payment Information'}
                        </h2>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t('orderDetail.paymentMethod') || 'Payment Method'}:
                            </span>
                            <span className="font-medium">
                              {order.payment.method === 'mm_qr' && 'MMQR'}
                              {order.payment.method === 'points' && 'EZ Point'}
                              {order.payment.method === 'ez_wallet' && 'EZ Wallet'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {t('orderDetail.paymentStatus') || 'Payment Status'}:
                            </span>
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(order.payment.status)}`}
                            >
                              {getStatusIcon(order.payment.status)}
                              {t(`orderHistory.status.${order.payment.status}`) ||
                                order.payment.status
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>

                          {/* Payment Amount Breakdown */}
                          {(() => {
                            const calculatedSubtotal =
                              order.subtotal ||
                              order.items.reduce(
                                (sum, item) =>
                                  sum +
                                  (item.price
                                    ? item.price * item.quantity
                                    : item.price * item.quantity),
                                0,
                              );
                            const calculatedDiscount =
                              order.discountAmount ||
                              order.items.reduce(
                                (sum, item) => sum + (item.discountAmount || 0) * item.quantity,
                                0,
                              );

                            return (
                              <>
                                {calculatedSubtotal > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">
                                      {t('orderDetail.subtotal') || 'Subtotal'}:
                                    </span>
                                    <span className="font-medium">
                                      {formatCurrency(calculatedSubtotal)}
                                    </span>
                                  </div>
                                )}

                                {calculatedDiscount > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">{discountLabel}:</span>
                                    <span className="font-medium text-green-600">
                                      {isMerchant ? '+' : '-'}
                                      {formatCurrency(calculatedDiscount)}
                                    </span>
                                  </div>
                                )}

                                <div className="flex justify-between pt-2 border-t border-gray-100">
                                  <span className="text-gray-600 font-semibold">
                                    {t('orderDetail.totalAmount') || 'Total Amount'}:
                                  </span>
                                  <span className="font-bold text-lg text-[#04BFBF]">
                                    {formatCurrency(order.totalAmount)}
                                  </span>
                                </div>

                                {/* Rewarded Points Display */}
                                {order.rewardedPoints && order.rewardedPoints !== 0 ? (
                                  <div className="flex justify-between pt-2 border-t border-gray-100">
                                    <span className="text-gray-600 font-semibold">
                                      {t('orderDetail.rewardedPoints') || 'Rewarded Points'}:
                                    </span>
                                    <span className="font-bold text-lg text-green-600">
                                      +{order.rewardedPoints} {t('common.points') || 'Points'}
                                    </span>
                                  </div>
                                ) : null}

                                {/* Total Refunded Amount Display */}
                                {order.totalRefundedAmount && order.totalRefundedAmount > 0 ? (
                                  <div className="flex justify-between pt-2 border-t border-gray-100">
                                    <span className="text-gray-600 font-semibold">
                                      {t('orderDetail.totalRefunded') || 'Refunded Amount'}:
                                    </span>
                                    <span className="font-bold text-lg text-red-600">
                                      {formatCurrency(order.totalRefundedAmount)}
                                    </span>
                                  </div>
                                ) : null}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="md:hidden space-y-4">
                      {/* Order Information Card */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">
                          {t('orderDetail.orderInfo') || 'Order Information'}
                        </h2>
                        <div className="space-y-3">
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-600 text-sm">
                              {t('orderDetail.orderNumber') || 'Order Number'}
                            </span>
                            <span className="font-medium text-sm">{order.orderNumber}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-600 text-sm">
                              {t('orderDetail.orderDate') || 'Order Date'}
                            </span>
                            <span className="font-medium text-sm">
                              {formatDate(order.createdAt)}
                            </span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-600 text-sm">
                              {t('orderDetail.totalItems') || 'Total Items'}
                            </span>
                            <span className="font-medium text-sm">{order.totalItems}</span>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <span className="text-gray-600 text-sm">
                              {t('orderDetail.orderStatus') || 'Status'}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}
                            >
                              {getStatusIcon(order.status)}
                              {t(`orderHistory.status.${order.status}`) ||
                                order.status
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Payment Information Card */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">
                          {t('orderDetail.paymentInfo') || 'Payment Information'}
                        </h2>
                        <div className="space-y-3">
                          <div className="flex justify-between py-2 border-b border-gray-100">
                            <span className="text-gray-600 text-sm">
                              {t('orderDetail.paymentMethod') || 'Payment Method'}
                            </span>
                            <span className="font-medium text-sm">
                              {order.payment.method === 'mm_qr' && 'MMQR'}
                              {order.payment.method === 'points' && 'Point'}
                              {order.payment.method === 'ez_wallet' && 'EZ Wallet'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span className="text-gray-600 text-sm">
                              {t('orderDetail.paymentStatus') || 'Payment Status'}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.payment.status)}`}
                            >
                              {getStatusIcon(order.payment.status)}
                              {t(`orderHistory.status.${order.payment.status}`) ||
                                order.payment.status
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </span>
                          </div>

                          {/* Payment Amount Breakdown */}
                          {(() => {
                            const calculatedSubtotal =
                              order.subtotal ||
                              order.items.reduce(
                                (sum, item) =>
                                  sum +
                                  (item.price
                                    ? item.price * item.quantity
                                    : item.price * item.quantity),
                                0,
                              );
                            const calculatedDiscount =
                              order.discountAmount ||
                              order.items.reduce(
                                (sum, item) => sum + (item.discountAmount || 0) * item.quantity,
                                0,
                              );

                            return (
                              <>
                                {calculatedSubtotal > 0 && (
                                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 text-sm">
                                      {t('orderDetail.subtotal') || 'Subtotal'}
                                    </span>
                                    <span className="font-medium text-sm">
                                      {formatCurrency(calculatedSubtotal)}
                                    </span>
                                  </div>
                                )}

                                {calculatedDiscount > 0 && (
                                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 text-sm">{discountLabel}</span>
                                    <span className="font-medium text-sm text-green-600">
                                      {isMerchant ? '+' : '-'}
                                      {formatCurrency(calculatedDiscount)}
                                    </span>
                                  </div>
                                )}

                                <div className="flex justify-between items-center py-2">
                                  <span className="text-gray-600 text-sm font-semibold">
                                    {t('orderDetail.totalAmount') || 'Total Amount'}
                                  </span>
                                  <span className="font-bold text-lg text-[#04BFBF]">
                                    {formatCurrency(order.totalAmount)}
                                  </span>
                                </div>

                                {/* Rewarded Points Display */}
                                {order.rewardedPoints && order.rewardedPoints !== 0 ? (
                                  <div className="flex justify-between items-center py-2 border-t border-gray-100">
                                    <span className="text-gray-600 text-sm font-semibold">
                                      {t('orderDetail.rewardedPoints') || 'Rewarded Points'}
                                    </span>
                                    <span className="font-bold text-lg text-green-600">
                                      +{order.rewardedPoints} {t('common.points') || 'Points'}
                                    </span>
                                  </div>
                                ) : null}

                                {/* Total Refunded Amount Display */}
                                {order.totalRefundedAmount && order.totalRefundedAmount > 0 ? (
                                  <div className="flex justify-between items-center py-2 border-t border-gray-100">
                                    <span className="text-gray-600 text-sm font-semibold">
                                      {t('orderDetail.totalRefunded') || 'Refunded Amount'}
                                    </span>
                                    <span className="font-bold text-lg text-red-600">
                                      {formatCurrency(order.totalRefundedAmount)}
                                    </span>
                                  </div>
                                ) : null}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Call Center Contact Information */}
                    {order.status === 'failed' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <svg
                              className="w-6 h-6 text-red-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-sm font-semibold text-red-800 mb-2">
                              {t('orderDetail.orderFailedTitle') || 'Order Failed - Need Help?'}
                            </h3>
                            <p className="text-sm text-red-700 mb-3">
                              {t('orderDetail.orderFailedMessage') ||
                                "We're sorry your order couldn't be completed. Our customer service team is here to help you resolve this issue."}
                            </p>
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <svg
                                  className="w-4 h-4 text-red-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                  />
                                </svg>
                                <a
                                  href={`tel:${t('orderDetail.callCenterPhone') || '+95 9 966 854 954'}`}
                                  className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                                >
                                  {t('orderDetail.callCenterPhone') || '+95 9 966 854 954'}
                                </a>
                              </div>
                              <div className="flex items-center space-x-2">
                                <svg
                                  className="w-4 h-4 text-red-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                  />
                                </svg>
                                <a
                                  href={`mailto:${t('orderDetail.callCenterEmail') || 'support@myeztopup.com'}`}
                                  className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                                >
                                  {t('orderDetail.callCenterEmail') || 'support@myeztopup.com'}
                                </a>
                              </div>
                              <div className="flex items-center space-x-2">
                                <svg
                                  className="w-4 h-4 text-red-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <span className="text-sm text-red-700">
                                  {t('orderDetail.availableHours') || 'Available 24/7'}
                                </span>
                              </div>

                              {/* Support Chat Button */}
                              <div className="mt-3">
                                <button
                                  onClick={() => {
                                    setIframeLoading(true);
                                    setIframeDialog({
                                      isOpen: true,
                                      url: 'https://tawk.to/chat/68c031ad93f9a019254a956f/1j4nc441q',
                                      title:
                                        t('orderDetail.chatWithSupport') || 'Chat with Support',
                                      type: 'instruction',
                                      fullWidth: true,
                                      fullHeight: true,
                                      showLanguageToggle: false,
                                    });
                                  }}
                                  className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm text-sm"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                    />
                                  </svg>
                                  {t('orderDetail.chatWithSupport') || 'Chat with Support'}
                                </button>

                                <div className="flex flex-col sm:flex-row mt-4 gap-4">
                                  {/* Chat via Viber (Button style) */}
                                  <a
                                    href={`viber://chat?number=959966854954`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-red-600 hover:bg-red-700 text-white font-normal py-2 px-4 rounded-lg transition-colors duration-200 inline-flex items-center justify-center gap-1 shadow-sm text-xs"
                                  >
                                    <HugeiconsIcon
                                      icon={ViberIcon}
                                      size={20}
                                      color="#fff"
                                      strokeWidth={1.5}
                                    />
                                    {t('orderDetail.chatViaViber') || 'Chat via Viber'}
                                  </a>

                                  {/* Chat via Messenger (Button style) */}
                                  <a
                                    href={`https://m.me/eztopupmm`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-red-600 hover:bg-red-700 text-white font-normal py-2 px-4 rounded-lg transition-colors duration-200 inline-flex items-center justify-center gap-1 shadow-sm text-xs"
                                  >
                                    <HugeiconsIcon
                                      icon={MessengerIcon}
                                      size={20}
                                      color="#fff"
                                      strokeWidth={1.5}
                                    />
                                    {t('orderDetail.chatViaMessenger') || 'Chat via Messenger'}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {/* Order Items Header with Collapse Button */}
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {t('orderDetail.orderItems') || 'Order Items'}
                        </h2>
                        <button
                          onClick={() => setIsOrderItemsCollapsed(!isOrderItemsCollapsed)}
                          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                        >
                          <span className="text-xs font-medium">
                            {isOrderItemsCollapsed
                              ? t('common.expand') || 'Expand'
                              : t('common.collapse') || 'Collapse'}
                          </span>
                          <svg
                            className={`w-4 h-4 transition-transform duration-200 ${
                              isOrderItemsCollapsed ? 'rotate-180' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Collapsible Content */}
                      <div
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${
                          isOrderItemsCollapsed ? 'max-h-20 opacity-60' : 'max-h-none opacity-100'
                        }`}
                      >
                        {isOrderItemsCollapsed ? (
                          /* Stack Preview - iOS Style */
                          <div className="relative">
                            {order.items.slice(0, 3).map((item, index) => (
                              <div
                                key={index}
                                className={`absolute inset-0 border border-gray-200 rounded-lg bg-white shadow-sm transition-all duration-200 ${
                                  index === 0
                                    ? 'z-30 transform translate-y-0'
                                    : index === 1
                                      ? 'z-20 transform translate-y-1 scale-[0.98]'
                                      : 'z-10 transform translate-y-2 scale-[0.96]'
                                }`}
                                style={{
                                  opacity: index === 0 ? 1 : index === 1 ? 0.8 : 0.6,
                                }}
                              >
                                <div className="p-3 flex items-center space-x-3">
                                  <div className="relative flex-shrink-0">
                                    <ImageWithFallback
                                      src={item.image}
                                      alt={item.name}
                                      className="w-10 h-10 object-cover rounded-lg border border-gray-100"
                                    />
                                    <div className="absolute -top-1 -right-1 bg-[#04BFBF] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-medium">
                                      {item.quantity}
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <h3 className="font-medium text-gray-900 text-sm truncate">
                                        {item.name}
                                      </h3>
                                      {item.fulfillmentStatus && (
                                        <div className="mt-1">
                                          <span
                                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                              item.fulfillmentStatus === 'fulfilled'
                                                ? 'bg-green-100 text-green-800'
                                                : item.fulfillmentStatus === 'failed'
                                                  ? 'bg-red-100 text-red-800'
                                                  : 'bg-gray-100 text-gray-800'
                                            }`}
                                          >
                                            {item.fulfillmentStatus === 'fulfilled'
                                              ? t('orderDetail.fulfilled') || 'Fulfilled'
                                              : item.fulfillmentStatus === 'failed'
                                                ? t('orderDetail.failed') || 'Failed'
                                                : item.fulfillmentStatus}
                                          </span>
                                        </div>
                                      )}
                                      {/* Discount Badge */}
                                      {item.discountAmount && item.discountAmount >= 1 ? (
                                        <span
                                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${discountBadgeClass}`}
                                        >
                                          {item.discountType === 'percentage' && item.discountValue
                                            ? `${isMerchant ? '+' : '-'}${item.discountValue}%`
                                            : `${isMerchant ? '+' : '-'}${formatCurrency(item.discountAmount ?? 0, item.currency)}`}
                                        </span>
                                      ) : (
                                        ''
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 truncate">
                                      {item.type.toUpperCase()}
                                    </p>
                                    {item.fx &&
                                      item.fx.exchangeRate &&
                                      typeof item.fx.exchangeRate === 'number' &&
                                      item.fx.exchangeRate > 0 && (
                                        <div className="mt-1 text-xs text-gray-500">
                                          <div className="flex items-center space-x-1">
                                            <span>
                                              1 {item.fx.productCurrencySymbol} ={' '}
                                              {item.fx.exchangeRate.toLocaleString()}{' '}
                                              {item.fx.baseCurrencyCode}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                  <div className="text-right">
                                    {/* Show original price if there's a discount */}
                                    {item.price &&
                                      item.discountAmount &&
                                      item.discountAmount >= 1 && (
                                        <div className="text-xs text-gray-500 line-through">
                                          {formatCurrency(
                                            item.price * item.quantity,
                                            item.currency,
                                          )}
                                        </div>
                                      )}

                                    {/* Show discount amount if exists */}
                                    {item.discountAmount && item.discountAmount >= 1 ? (
                                      <span
                                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${discountBadgeClass}`}
                                      >
                                        {item.discountType === 'percentage' && item.discountValue
                                          ? `${isMerchant ? '+' : '-'}${item.discountValue}%`
                                          : `${isMerchant ? '+' : '-'}${formatCurrency(item.discountAmount ?? 0, item.currency)}`}
                                      </span>
                                    ) : (
                                      ''
                                    )}

                                    <span className="text-sm font-bold text-[#04BFBF]">
                                      {formatCurrency(item.subtotal, item.currency)}
                                    </span>
                                    {item.fx &&
                                      item.fx.exchangeRate &&
                                      typeof item.fx.exchangeRate === 'number' &&
                                      item.fx.exchangeRate > 0 &&
                                      item.fx.exchangeDateTime &&
                                      !isNaN(new Date(item.fx.exchangeDateTime).getTime()) && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          {new Date(item.fx.exchangeDateTime).toLocaleDateString(
                                            'en-US',
                                            { month: 'short', day: 'numeric' },
                                          )}
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {/* Placeholder for stacking effect */}
                            <div className="h-16 bg-transparent"></div>
                            {order.items.length > 3 && (
                              <div className="absolute bottom-0 right-2 bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                                +{order.items.length - 3} more
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {/* Desktop Layout */}
                            <div className="hidden md:block space-y-4">
                              {order.items.map((item, index) => (
                                <div
                                  key={index}
                                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                                >
                                  <div className="flex items-center space-x-4">
                                    <div className="relative">
                                      {networkStatus.isOnline ? (
                                        <>
                                          <ImageWithFallback
                                            src={item.image}
                                            alt={item.name}
                                            className="w-16 h-16 object-cover rounded-lg border border-gray-100 bg-gray-50"
                                          />
                                          <div className="absolute -top-2 -right-2 bg-blue-500 border-2 border-white text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-medium">
                                            {item.quantity}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                                          <span className="text-gray-500 font-medium text-lg">
                                            {item.quantity}x
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <div>
                                          <h3 className="font-semibold text-gray-900">
                                            {item.name}
                                          </h3>
                                          {item.fulfillmentStatus && (
                                            <div className="mt-1">
                                              <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                  item.fulfillmentStatus === 'fulfilled'
                                                    ? 'bg-green-100 text-green-800'
                                                    : item.fulfillmentStatus === 'failed'
                                                      ? 'bg-red-100 text-red-800'
                                                      : 'bg-gray-100 text-gray-800'
                                                }`}
                                              >
                                                {item.fulfillmentStatus === 'fulfilled'
                                                  ? t('orderDetail.fulfilled') || 'Fulfilled'
                                                  : item.fulfillmentStatus === 'failed'
                                                    ? t('orderDetail.failed') || 'Failed'
                                                    : item.fulfillmentStatus}
                                              </span>
                                              {item.fulfillmentStatus === 'failed' &&
                                                item.failureReason && (
                                                  <div className="text-xs text-red-600 mt-1">
                                                    {item.failureReason}
                                                  </div>
                                                )}
                                            </div>
                                          )}
                                        </div>
                                        {/* Discount Badge */}
                                        {item.discountAmount && item.discountAmount >= 1 ? (
                                          <span
                                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${discountBadgeClass}`}
                                          >
                                            {item.discountType === 'percentage' &&
                                            item.discountValue
                                              ? `${isMerchant ? '+' : '-'}${item.discountValue}%`
                                              : `${isMerchant ? '+' : '-'}${formatCurrency(item.discountAmount ?? 0, item.currency)}`}
                                          </span>
                                        ) : (
                                          ''
                                        )}
                                        {item.instruction && (
                                          <button
                                            onClick={() =>
                                              handleShowInstruction(item.instruction!, item.name)
                                            }
                                            className="text-blue-500 hover:text-blue-700 transition-colors"
                                            title={
                                              t('orderDetail.viewInstructions') ||
                                              'View Instructions'
                                            }
                                          >
                                            <svg
                                              className="w-4 h-4"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                              />
                                            </svg>
                                          </button>
                                        )}
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                          <svg
                                            className="w-3 h-3 mr-1"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                                            />
                                          </svg>
                                          {item.type.toUpperCase()}
                                        </span>
                                        {item.redeemUrl && (
                                          <button
                                            onClick={() => handleRedeem(item.redeemUrl!, item.name)}
                                            className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-[#04BFBF] text-white hover:bg-[#04BFBF]/80 transition-colors"
                                          >
                                            <svg
                                              className="w-3 h-3 mr-1"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                              />
                                            </svg>
                                            {t('orderDetail.redeem') || 'Redeem'}
                                          </button>
                                        )}
                                      </div>
                                      {item.fx &&
                                        item.fx.exchangeRate &&
                                        typeof item.fx.exchangeRate === 'number' &&
                                        item.fx.exchangeRate > 0 &&
                                        item.fx.exchangeDateTime &&
                                        !isNaN(new Date(item.fx.exchangeDateTime).getTime()) && (
                                          <div className="mt-2 text-xs text-gray-500">
                                            <div className="flex items-center space-x-2">
                                              <span>
                                                1 {item.fx.productCurrencySymbol} ={' '}
                                                {item.fx.exchangeRate.toLocaleString()}{' '}
                                                {item.fx.baseCurrencyCode}
                                              </span>
                                              <span className="text-gray-400">•</span>
                                              <span>
                                                {new Date(
                                                  item.fx.exchangeDateTime,
                                                ).toLocaleDateString('en-US', {
                                                  month: 'short',
                                                  day: 'numeric',
                                                })}
                                              </span>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                    <div className="text-right space-y-1">
                                      <div className="text-sm text-gray-500">
                                        {t('orderDetail.unitPrice') || 'Unit Price'}
                                      </div>
                                      <div className="text-sm font-medium text-gray-700">
                                        {formatCurrency(
                                          item.discountPrice || item.price,
                                          item.currency,
                                        )}
                                      </div>
                                      <div className="text-lg font-bold text-[#04BFBF] border-t border-gray-100 pt-1">
                                        {formatCurrency(item.subtotal, item.currency)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Mobile Layout */}
                            <div className="md:hidden space-y-3">
                              {order.items.map((item, index) => (
                                <div
                                  key={index}
                                  className="border border-gray-200 rounded-lg p-3 bg-white"
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="relative flex-shrink-0">
                                      {networkStatus.isOnline ? (
                                        <>
                                          <ImageWithFallback
                                            src={item.image}
                                            alt={item.name}
                                            className="w-12 h-12 object-cover rounded-lg border border-gray-100"
                                          />
                                          <div className="absolute -top-1 -right-1 bg-[#04BFBF] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                                            {item.quantity}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="w-12 h-12 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                                          <span className="text-gray-500 font-medium text-sm">
                                            {item.quantity}x
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2 mb-1">
                                        <h3 className="font-semibold text-gray-900 text-sm truncate">
                                          {item.name}
                                        </h3>
                                        {item.fulfillmentStatus && (
                                          <div className="mt-1 mb-1">
                                            <span
                                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                item.fulfillmentStatus === 'fulfilled'
                                                  ? 'bg-green-100 text-green-800'
                                                  : item.fulfillmentStatus === 'failed'
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-gray-100 text-gray-800'
                                              }`}
                                            >
                                              {item.fulfillmentStatus === 'fulfilled'
                                                ? t('orderDetail.fulfilled') || 'Fulfilled'
                                                : item.fulfillmentStatus === 'failed'
                                                  ? t('orderDetail.failed') || 'Failed'
                                                  : item.fulfillmentStatus}
                                            </span>
                                            {item.fulfillmentStatus === 'failed' &&
                                              item.failureReason && (
                                                <div className="text-[10px] text-red-600 mt-0.5 whitespace-normal">
                                                  {item.failureReason}
                                                </div>
                                              )}
                                          </div>
                                        )}
                                        {item.instruction && (
                                          <button
                                            onClick={() =>
                                              handleShowInstruction(item.instruction!, item.name)
                                            }
                                            className="text-blue-500 hover:text-blue-700 transition-colors flex-shrink-0"
                                            title={
                                              t('orderDetail.viewInstructions') ||
                                              'View Instructions'
                                            }
                                          >
                                            <svg
                                              className="w-4 h-4"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                              />
                                            </svg>
                                          </button>
                                        )}
                                      </div>
                                      <div className="mb-2 flex items-center space-x-2">
                                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                                          <svg
                                            className="w-3 h-3 mr-1"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth={2}
                                              d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                                            />
                                          </svg>
                                          {item.type.toUpperCase()}
                                        </span>
                                        {item.redeemUrl && (
                                          <button
                                            onClick={() => handleRedeem(item.redeemUrl!, item.name)}
                                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-[#04BFBF] text-white hover:bg-[#04BFBF]/80 transition-colors"
                                          >
                                            <svg
                                              className="w-3 h-3 mr-1"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                              />
                                            </svg>
                                            {t('orderDetail.redeem') || 'Redeem'}
                                          </button>
                                        )}
                                      </div>
                                      <div className="space-y-1">
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs text-gray-500">
                                            {t('orderDetail.unitPrice') || 'Unit Price'}
                                          </span>
                                          <div className="text-right">
                                            {/* Show original price if there's a discount */}
                                            {item.price &&
                                            item.discountAmount &&
                                            item.discountAmount >= 1 ? (
                                              <div className="text-xs text-gray-500 line-through">
                                                {item.price.toLocaleString()} MMK
                                              </div>
                                            ) : (
                                              ''
                                            )}
                                            <span className="text-xs font-medium text-gray-700">
                                              {item.price.toLocaleString()} MMK
                                            </span>
                                          </div>
                                        </div>

                                        <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                                          <span className="text-sm font-medium text-gray-900">
                                            {t('orderDetail.subtotal') || 'Subtotal'}
                                          </span>
                                          <span className="text-sm font-bold text-[#04BFBF]">
                                            {formatCurrency(item.subtotal, item.currency)}
                                          </span>
                                        </div>
                                        {item.fx &&
                                          item.fx.exchangeRate &&
                                          typeof item.fx.exchangeRate === 'number' &&
                                          item.fx.exchangeRate > 0 &&
                                          item.fx.exchangeDateTime &&
                                          !isNaN(new Date(item.fx.exchangeDateTime).getTime()) && (
                                            <div className="mt-2 flex justify-between text-xs text-gray-500">
                                              <div className="flex items-center space-x-2">
                                                <span>
                                                  1 {item.fx.productCurrencySymbol} ={' '}
                                                  {item.fx.exchangeRate.toLocaleString()}{' '}
                                                  {item.fx.baseCurrencyCode}
                                                </span>
                                                <span className="text-gray-400">•</span>
                                                <span>
                                                  {new Date(
                                                    item.fx.exchangeDateTime,
                                                  ).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                  })}
                                                </span>
                                              </div>
                                              {/* Show discount information if exists */}
                                              {item.discountAmount && item.discountAmount >= 1 ? (
                                                <span
                                                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${discountBadgeClass}`}
                                                >
                                                  {item.discountType === 'percentage' &&
                                                  item.discountValue
                                                    ? `${isMerchant ? '+' : '-'}${item.discountValue}%`
                                                    : `${isMerchant ? '+' : '-'}${formatCurrency(item.discountAmount ?? 0, item.currency)}`}
                                                </span>
                                              ) : (
                                                ''
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Card List Section */}
                    {order.cards && order.cards.length > 0 && (
                      <div className="space-y-4">
                        {/* Card List Header with Collapse Button and Individual Print Buttons */}
                        <div className="flex items-center justify-between">
                          <h2 className="text-lg font-semibold text-gray-900">
                            {t('orderDetail.cardList') || 'Card List'}
                          </h2>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setIsCardListCollapsed(!isCardListCollapsed)}
                              className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            >
                              <span className="text-xs font-medium">
                                {isCardListCollapsed
                                  ? t('common.expand') || 'Expand'
                                  : t('common.collapse') || 'Collapse'}
                              </span>
                              <svg
                                className={`w-4 h-4 transition-transform duration-200 ${
                                  isCardListCollapsed ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* Collapsible Content */}
                        <div
                          className={`transition-all duration-300 ease-in-out overflow-hidden ${
                            isCardListCollapsed ? 'max-h-20 opacity-60' : 'max-h-none opacity-100'
                          }`}
                        >
                          {isCardListCollapsed ? (
                            /* Stack Preview - iOS Style */
                            <div className="relative">
                              {order.cards.slice(0, 3).map((card, index) => (
                                <div
                                  key={card._id}
                                  className={`absolute inset-0 border border-gray-200 rounded-lg bg-white shadow-sm transition-all duration-200 ${
                                    index === 0
                                      ? 'z-30 transform translate-y-0'
                                      : index === 1
                                        ? 'z-20 transform translate-y-1 scale-[0.98]'
                                        : 'z-10 transform translate-y-2 scale-[0.96]'
                                  }`}
                                  style={{
                                    opacity: index === 0 ? 1 : index === 1 ? 0.8 : 0.6,
                                  }}
                                >
                                  <div className="p-3 flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-[#04BFBF]/10 rounded-lg flex items-center justify-center">
                                        <svg
                                          className="w-4 h-4 text-[#04BFBF]"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                          />
                                        </svg>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-gray-900 text-sm truncate">
                                          {getCardNameByProductId(card.productId)}
                                        </h3>
                                        <p className="text-xs text-gray-500 truncate font-mono">
                                          {card.serialNumber}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <span className="text-xs text-gray-500">
                                        {card.expireDate
                                          ? new Date(card.expireDate).toLocaleDateString('en-US', {
                                              month: 'short',
                                              day: 'numeric',
                                            })
                                          : 'No Expiry'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {/* Placeholder for stacking effect */}
                              <div className="h-16 bg-transparent"></div>
                              {order.cards.length > 3 && (
                                <div className="absolute bottom-0 right-2 bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                                  +{order.cards.length - 3} more
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              {/* Desktop Table View */}
                              <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-auto">
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y overflow-x-auto divide-gray-200">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          {t('orderDetail.cardName') || 'Name'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          {t('orderDetail.serialNumber') || 'Serial Number'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          <div className="flex items-center gap-2 relative group">
                                            {t('orderDetail.pinNumber') || 'PIN Number'}
                                            <div className="relative">
                                              <svg
                                                className="w-4 h-4 text-blue-500 cursor-help"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth={2}
                                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                              </svg>
                                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white !capitalize !text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 z-[9999]">
                                                {t('orderDetail.scratchWarning') ||
                                                  'Once you scratch a card to reveal the PIN, sharing will be disabled for security reasons.'}
                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                                              </div>
                                            </div>
                                          </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          {t('orderDetail.expireDate') || 'Expire Date'}
                                        </th>
                                        {user?.activeRole === 'merchant' && (
                                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Print
                                          </th>
                                        )}
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {order.cards.map((card, index) => (
                                        <tr
                                          key={card._id}
                                          className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                        >
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <div className="font-medium">
                                              {getCardNameByProductId(card.productId)}
                                            </div>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                                            {card.serialNumber}
                                          </td>
                                          <td className="px-6 py-6 whitespace-nowrap text-sm text-gray-900 font-mono">
                                            <div className="flex items-center justify-start">
                                              <ScratchCard
                                                cardId={card._id}
                                                pinNumber={card.pinNumber}
                                                isScratched={scratchedPins.has(card._id)}
                                                onScratch={() => handleScratch(card._id)}
                                              />
                                            </div>
                                          </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {card.expireDate ? (
                                              new Date(card.expireDate).toLocaleDateString(
                                                'en-US',
                                                {
                                                  year: 'numeric',
                                                  month: 'short',
                                                  day: 'numeric',
                                                },
                                              )
                                            ) : (
                                              <span className="text-green-600 font-medium">
                                                {t('orderDetail.notExpired') || 'Not Expired'}
                                              </span>
                                            )}
                                          </td>
                                          {user?.activeRole === 'merchant' && (
                                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                              <button
                                                onClick={() => {
                                                  // Find the item that matches this card's productId
                                                  const matchingItem = order.items.find(
                                                    (item) => item.productId === card.productId,
                                                  );

                                                  if (matchingItem) {
                                                    // Create a temporary PrintReceiptItem component and trigger print
                                                    const existingActivePrint =
                                                      document.querySelector('.print-only-active');
                                                    if (existingActivePrint) {
                                                      try {
                                                        (
                                                          existingActivePrint as HTMLElement
                                                        ).remove();
                                                      } catch (error) {
                                                        logger.warn(
                                                          'Failed to remove existing active print element:',
                                                          error,
                                                        );
                                                      }
                                                    }

                                                    const printElement =
                                                      document.createElement('div');
                                                    printElement.className = 'print-only-active';
                                                    document.body.appendChild(printElement);

                                                    // Use React to render the component
                                                    import('react-dom/client').then(
                                                      ({ createRoot }) => {
                                                        const root = createRoot(printElement);
                                                        root.render(
                                                          createElement(PrintReceiptItem, {
                                                            orderNumber: order.orderNumber,
                                                            createdAt: order.createdAt,
                                                            item: matchingItem,
                                                            cards: [card], // Pass only this specific card
                                                            getProductTypeByProductId:
                                                              getProductTypeByProductId,
                                                          }),
                                                        );

                                                        const cleanup = () => {
                                                          try {
                                                            root.unmount();
                                                          } catch (error) {
                                                            logger.warn(
                                                              'Print cleanup failed during unmount:',
                                                              error,
                                                            );
                                                          }
                                                          try {
                                                            document.body.removeChild(printElement);
                                                          } catch (error) {
                                                            logger.warn(
                                                              'Print cleanup failed removing print element:',
                                                              error,
                                                            );
                                                          }
                                                        };

                                                        window.addEventListener(
                                                          'afterprint',
                                                          cleanup,
                                                          { once: true },
                                                        );

                                                        // Alternative print approach using iframe with mobile-style formatting
                                                        const printContent = () => {
                                                          console.log(
                                                            '=== DESKTOP PRINT DEBUG START ===',
                                                          );
                                                          console.log('Order data:', order);
                                                          console.log(
                                                            'Matching item:',
                                                            matchingItem,
                                                          );
                                                          console.log('Card data:', [card]);

                                                          // Create HTML content for printing with mobile-style formatting
                                                          const printHTML = `
                                                          <!DOCTYPE html>
                                                          <html>
                                                          <head>
                                                            <meta charset="utf-8">
                                                            <title>Print Receipt</title>
                                                            <style>
                                                              @page {
                                                                 size: auto;
                                                                 margin: 1mm;
                                                               }
                                                              body {
                                                                font-family: Arial, sans-serif;
                                                                font-size: 12px;
                                                                line-height: 1.4;
                                                                color: black;
                                                                background: white;
                                                                width: 80mm;
                                                                margin: 0 auto;
                                                                padding: 2mm;
                                                              }
                                                              .print-container {
                                                                width: 100%;
                                                                background: white;
                                                              }
                                                              .print-header {
                                                                text-align: center;
                                                                border-bottom: 1px solid black;
                                                                padding-bottom: 3px;
                                                                margin-bottom: 4px;
                                                              }
                                                              .print-logo img {
                                                                width: 40mm;
                                                                height: auto;
                                                                display: block;
                                                                margin: 0 auto 8px auto;
                                                              }
                                                              .print-title {
                                                                font-size: 20px;
                                                                font-weight: bold;
                                                                margin-bottom: 8px;
                                                              }
                                                              .print-subtitle {
                                                                font-size: 16px;
                                                                margin-bottom: 8px;
                                                              }
                                                              .print-row {
                                                                display: flex;
                                                                justify-content: space-between;
                                                                margin-bottom: 4px;
                                                                font-size: 16px;
                                                              }
                                                              .print-divider {
                                                                border-top: 1px solid black;
                                                                margin: 8px 0;
                                                              }
                                                              .print-card {
                                                                border: 1px solid black;
                                                                padding: 3px;
                                                                margin-bottom: 4px;
                                                                page-break-inside: avoid;
                                                              }
                                                              .print-card-header {
                                                                font-weight: bold;
                                                                text-align: center;
                                                                margin-bottom: 2px;
                                                                border-bottom: 1px solid black;
                                                                padding-bottom: 2px;
                                                                font-size: 18px;
                                                              }
                                                              .print-pin {
                                                                font-size: 18px;
                                                                font-weight: bold;
                                                                text-align: center;
                                                                background: #f0f0f0;
                                                                padding: 2px;
                                                                margin: 4px 0 2px 0;
                                                                border: 1px solid black;
                                                                word-break: break-all;
                                                                letter-spacing: 1px;
                                                              }
                                                              .print-total {
                                                                border-top: 2px solid black;
                                                                border-bottom: 2px solid black;
                                                                padding: 3px 0;
                                                                font-weight: bold;
                                                                text-align: center;
                                                                margin: 4px 0;
                                                                font-size: 18px;
                                                              }
                                                              .print-footer {
                                                                text-align: center;
                                                                margin-top: 4px;
                                                                border-top: 1px solid black;
                                                                padding-top: 3px;
                                                                font-size: 12px;
                                                                line-height: 1.3;
                                                              }
                                                            </style>
                                                          </head>
                                                          <body>
                                                            <div id="print-content"></div>
                                                          </body>
                                                          </html>
                                                        `;

                                                          // Set print dialog state for desktop with mobile-style formatting
                                                          setPrintDialog({
                                                            isOpen: true,
                                                            type: 'desktop',
                                                            content: printHTML,
                                                            orderData: {
                                                              order,
                                                              matchingItem,
                                                              card,
                                                              getProductTypeByProductId,
                                                            },
                                                          });
                                                        };

                                                        printContent();
                                                      },
                                                    );
                                                  }
                                                }}
                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                              >
                                                <svg
                                                  className="w-4 h-4 mr-2"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                                                  />
                                                </svg>
                                                Print
                                              </button>
                                            </td>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {/* Mobile Card View */}
                              <div className="md:hidden space-y-4">
                                {order.cards.map((card) => (
                                  <div
                                    key={card._id}
                                    className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                                  >
                                    <div className="space-y-3">
                                      {/* Card Name */}
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          {t('orderDetail.cardName') || 'Name'}
                                        </span>
                                        <span className="text-sm text-gray-900 font-medium">
                                          {getCardNameByProductId(card.productId)}
                                        </span>
                                      </div>

                                      {/* Serial Number */}
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          {t('orderDetail.serialNumber') || 'Serial Number'}
                                        </span>
                                        <span className="text-sm text-gray-900 font-mono">
                                          {card.serialNumber}
                                        </span>
                                      </div>

                                      {/* PIN Number */}
                                      <div className="space-y-2">
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                                          {t('orderDetail.pinNumber') || 'PIN Number'}
                                        </span>
                                        <div className="flex justify-center">
                                          <ScratchCard
                                            cardId={card._id}
                                            pinNumber={card.pinNumber}
                                            isScratched={scratchedPins.has(card._id)}
                                            onScratch={() => handleScratch(card._id)}
                                          />
                                        </div>
                                      </div>

                                      {/* Expire Date */}
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                          {t('orderDetail.expireDate') || 'Expire Date'}
                                        </span>
                                        <span className="text-sm text-gray-900">
                                          {card.expireDate ? (
                                            new Date(card.expireDate).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                            })
                                          ) : (
                                            <span className="text-green-600 font-medium">
                                              {t('orderDetail.notExpired') || 'Not Expired'}
                                            </span>
                                          )}
                                        </span>
                                      </div>

                                      {/* Action Button */}
                                      {user?.activeRole === 'merchant' && (
                                        <div className="pt-3 border-t border-gray-200">
                                          <button
                                            onClick={() => {
                                              // Find the item that matches this card's productId
                                              const matchingItem = order.items.find(
                                                (item) => item.productId === card.productId,
                                              );

                                              if (matchingItem) {
                                                // Create a temporary PrintReceiptItem component and trigger print
                                                const existingActivePrint =
                                                  document.querySelector('.print-only-active');
                                                if (existingActivePrint) {
                                                  try {
                                                    (existingActivePrint as HTMLElement).remove();
                                                  } catch (error) {
                                                    logger.warn(
                                                      'Failed to remove existing active print element (mobile):',
                                                      error,
                                                    );
                                                  }
                                                }

                                                const printElement = document.createElement('div');
                                                printElement.className = 'print-only-active';
                                                document.body.appendChild(printElement);

                                                // Use React to render the component
                                                import('react-dom/client').then(
                                                  ({ createRoot }) => {
                                                    const root = createRoot(printElement);
                                                    root.render(
                                                      createElement(PrintReceiptItem, {
                                                        orderNumber: order.orderNumber,
                                                        createdAt: order.createdAt,
                                                        item: matchingItem,
                                                        cards: [card], // Pass only this specific card
                                                        getProductTypeByProductId:
                                                          getProductTypeByProductId,
                                                      }),
                                                    );

                                                    const cleanup = () => {
                                                      try {
                                                        root.unmount();
                                                      } catch (error) {
                                                        logger.warn(
                                                          'Mobile print cleanup failed during unmount:',
                                                          error,
                                                        );
                                                      }
                                                      try {
                                                        document.body.removeChild(printElement);
                                                      } catch (error) {
                                                        logger.warn(
                                                          'Mobile print cleanup failed removing print element:',
                                                          error,
                                                        );
                                                      }
                                                    };

                                                    window.addEventListener('afterprint', cleanup, {
                                                      once: true,
                                                    });

                                                    requestAnimationFrame(() => {
                                                      requestAnimationFrame(() => {
                                                        // Add a longer delay to ensure React rendering is complete
                                                        setTimeout(() => {
                                                          console.log(
                                                            'Mobile print: Starting print process...',
                                                          );
                                                          console.log(
                                                            'Mobile print: Order data:',
                                                            order,
                                                          );
                                                          console.log(
                                                            'Mobile print: Matching item:',
                                                            matchingItem,
                                                          );
                                                          console.log(
                                                            'Mobile print: Card data:',
                                                            card,
                                                          );

                                                          // Create HTML content for printing
                                                          const printHTML = `
                                                        <!DOCTYPE html>
                                                        <html>
                                                        <head>
                                                          <meta charset="utf-8">
                                                          <title>Print Receipt</title>
                                                          <style>
                                                            @page {
                                                               size: auto;
                                                               margin: 1mm;
                                                             }
                                                            body {
                                                              margin: 0;
                                                              padding: 3px;
                                                              font-family: 'Courier New', monospace;
                                                              font-size: 18px;
                                                              line-height: 1.1;
                                                              color: black;
                                                              background: white;
                                                              width: 100%;
                                                              max-width: 80mm;
                                                              box-sizing: border-box;
                                                            }
                                                            .print-container {
                                                              width: 100%;
                                                              background: white;
                                                            }
                                                            .print-header {
                                                              text-align: center;
                                                              border-bottom: 1px solid black;
                                                              padding-bottom: 3px;
                                                              margin-bottom: 4px;
                                                            }
                                                            .print-logo img {
                                                              width: 40mm;
                                                              height: auto;
                                                              display: block;
                                                              margin: 0 auto 8px auto;
                                                            }
                                                            .print-title {
                                                              font-size: 20px;
                                                              font-weight: bold;
                                                              margin-bottom: 8px;
                                                            }
                                                            .print-subtitle {
                                                              font-size: 16px;
                                                              margin-bottom: 8px;
                                                            }
                                                            .print-row {
                                                              display: flex;
                                                              justify-content: space-between;
                                                              margin-bottom: 4px;
                                                              font-size: 16px;
                                                            }
                                                            .print-divider {
                                                              border-top: 1px solid black;
                                                              margin: 8px 0;
                                                            }
                                                            .print-card {
                                                              border: 1px solid black;
                                                              padding: 3px;
                                                              margin-bottom: 4px;
                                                              page-break-inside: avoid;
                                                            }
                                                            .print-card-header {
                                                              font-weight: bold;
                                                              text-align: center;
                                                              margin-bottom: 2px;
                                                              border-bottom: 1px solid black;
                                                              padding-bottom: 2px;
                                                              font-size: 18px;
                                                            }
                                                            .print-pin {
                                                              font-size: 18px;
                                                              font-weight: bold;
                                                              text-align: center;
                                                              background: #f0f0f0;
                                                              padding: 2px;
                                                              margin: 4px 0 2px 0;
                                                              border: 1px solid black;
                                                              word-break: break-all;
                                                              letter-spacing: 1px;
                                                            }
                                                            .print-total {
                                                              border-top: 2px solid black;
                                                              border-bottom: 2px solid black;
                                                              padding: 3px 0;
                                                              font-weight: bold;
                                                              text-align: center;
                                                              margin: 4px 0;
                                                              font-size: 18px;
                                                            }
                                                            .print-footer {
                                                              text-align: center;
                                                              margin-top: 4px;
                                                              border-top: 1px solid black;
                                                              padding-top: 3px;
                                                              font-size: 12px;
                                                              line-height: 1.3;
                                                            }
                                                          </style>
                                                        </head>
                                                        <body>
                                                          <div id="print-content"></div>
                                                        </body>
                                                        </html>
                                                      `;

                                                          // Set print dialog state for mobile
                                                          setPrintDialog({
                                                            isOpen: true,
                                                            type: 'mobile',
                                                            content: printHTML,
                                                            orderData: {
                                                              order,
                                                              matchingItem,
                                                              card,
                                                              getProductTypeByProductId,
                                                            },
                                                          });
                                                        }, 500); // Added 500ms delay
                                                      });
                                                    });
                                                  },
                                                );
                                              }
                                            }}
                                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                          >
                                            <svg
                                              className="w-4 h-4 mr-2"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                                              />
                                            </svg>
                                            Print
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Eload Result Section for Completed Orders with Eload Results */}
                    {order.status.toLowerCase() === 'completed' && order.eload && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <svg
                              className="w-8 h-8 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-green-900">
                              {t('orderDetail.orderCompleted') || 'Order Completed Successfully!'}
                            </h3>
                            <div className="mt-2">
                              <p
                                className="text-sm text-green-700 font-medium"
                                dangerouslySetInnerHTML={{
                                  __html: formatMessageWithBoldPhoneNumbers(order.eload.message),
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Download Section for Completed Orders without Eload Results */}
                    {order.status.toLowerCase() === 'completed' && !order.eload && (
                      <div
                        className={`${order.cardListPdfUrl ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'} rounded-lg p-6`}
                      >
                        <div className="block md:flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <svg
                                className={`w-8 h-8 ${order.cardListPdfUrl ? 'text-green-600' : 'text-blue-600'}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <div>
                              <h3
                                className={`text-lg font-semibold ${order.cardListPdfUrl ? 'text-green-900' : 'text-blue-900'}`}
                              >
                                {t('orderDetail.orderCompleted') || 'Order Completed Successfully!'}
                              </h3>
                              <div className="flex items-center">
                                <p
                                  className={`text-sm ${order.cardListPdfUrl ? 'text-green-700' : 'text-blue-700'} mt-1`}
                                >
                                  {order.cardListPdfUrl
                                    ? t('orderDetail.downloadCardList') ||
                                      'Your card list is ready for download'
                                    : t('orderDetail.generateInvoice') ||
                                      'Generate your invoice to download'}
                                </p>
                                <div className="relative group ml-2">
                                  <svg
                                    className="w-4 h-4 text-blue-500 cursor-help"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 z-[9999]">
                                    {t('orderDetail.invoiceWarning') ||
                                      'Generating an invoice will disable sharing functionality.'}
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="w-full mt-4 md:mt-0 md:w-auto md:flex-shrink-0 flex flex-col md:flex-row gap-2">
                            {/* Print Button */}
                            {/* <button
                              onClick={async () => {
                                // Check if any cards are scratched
                                if (hasAnyCardScratched()) {
                                  showToast(
                                    'error',
                                    t('orderDetail.cannotPrintScratched') ||
                                    'Cannot print receipt after cards have been scratched',
                                  );
                                  return;
                                }

                                if (order.cards && order.cards.length > 0) {
                                  window.print();
                                } else {
                                  showToast('error', 'No cards available to print');
                                }
                              }}
                              disabled={hasAnyCardScratched()}
                              className={`inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm ${hasAnyCardScratched()
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                            >
                              <svg
                                className="w-4 h-4 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                                />
                              </svg>
                              Print Receipt
                            </button> */}

                            {order.cardListPdfUrl ? (
                              <a
                                onClick={async () => {
                                  if (downloadingPdf) return;

                                  try {
                                    setDownloadingPdf(true);
                                    // Download the PDF using the existing URL
                                    const downloadResponse = await fetch(order.cardListPdfUrl!);

                                    if (!downloadResponse.ok) {
                                      throw new Error('Failed to download PDF');
                                    }

                                    const blob = await downloadResponse.blob();
                                    const url = window.URL.createObjectURL(blob);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute(
                                      'download',
                                      `${order.orderNumber}-myEZTopup.pdf`,
                                    );
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                  } catch (error) {
                                    logger.error('Download failed:', error);
                                    showToast('error', 'PDF download failed. Please try again.');
                                  } finally {
                                    setDownloadingPdf(false);
                                  }
                                }}
                                className={`inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm ${
                                  downloadingPdf
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-green-600 hover:bg-green-700 cursor-pointer'
                                }`}
                              >
                                {downloadingPdf ? (
                                  <>
                                    <svg
                                      className="w-4 h-4 mr-2 animate-spin"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    {t('orderDetail.downloading') || 'Downloading...'}
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      className="w-4 h-4 mr-2"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                      />
                                    </svg>
                                    {t('orderDetail.downloadPDF') || 'Download PDF'}
                                  </>
                                )}
                              </a>
                            ) : (
                              <button
                                onClick={async () => {
                                  if (generatingPdf) return;

                                  try {
                                    setGeneratingPdf(true);
                                    // Call the API to generate PDF
                                    const baseUrl = import.meta.env.VITE_BASE_URL;
                                    const token = localStorage.getItem('token');

                                    const generateResponse = await fetch(
                                      `${baseUrl}/api/v1/cards/pdf/${order._id}`,
                                      {
                                        method: 'GET',
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                          'Content-Type': 'application/json',
                                        },
                                      },
                                    );

                                    if (!generateResponse.ok) {
                                      throw new Error('Failed to generate PDF');
                                    }

                                    const generateData = await generateResponse.json();
                                    const pdfUrl = generateData.data?.pdfUrl;

                                    if (!pdfUrl) {
                                      throw new Error('PDF URL not found in response');
                                    }

                                    // Update the order state with the new PDF URL
                                    setOrder((prevOrder) => {
                                      if (prevOrder) {
                                        return {
                                          ...prevOrder,
                                          cardListPdfUrl: pdfUrl,
                                        };
                                      }
                                      return prevOrder;
                                    });

                                    showToast('success', 'Invoice generated successfully!');
                                  } catch (error) {
                                    logger.error('Generate PDF failed:', error);
                                    showToast(
                                      'error',
                                      'Failed to generate invoice. Please try again.',
                                    );
                                  } finally {
                                    setGeneratingPdf(false);
                                  }
                                }}
                                className={`inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm ${
                                  generatingPdf
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                                }`}
                                disabled={generatingPdf}
                              >
                                {generatingPdf ? (
                                  <>
                                    <svg
                                      className="w-4 h-4 mr-2 animate-spin"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    {t('orderDetail.generatingInvoice') || 'Generating...'}
                                  </>
                                ) : (
                                  <>
                                    <svg
                                      className="w-4 h-4 mr-2"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                      />
                                    </svg>
                                    {t('orderDetail.generateInvoice') || 'Generate Invoice'}
                                  </>
                                )}
                              </button>
                            )}

                            {/* Print All Receipt Button */}
                            {user?.activeRole === 'merchant' && (
                              <button
                                onClick={async () => {
                                  // Check if any cards are scratched
                                  if (order.cards?.some((card) => card.scratch === true)) {
                                    showToast(
                                      'error',
                                      t('orderDetail.cannotPrintScratched') ||
                                        'Cannot print receipt after cards have been scratched',
                                    );
                                    return;
                                  }

                                  if (!order.cards || order.cards.length === 0) {
                                    showToast('error', 'No cards available to print');
                                    return;
                                  }

                                  try {
                                    // Create HTML content for iframe similar to individual print
                                    const printHTML = `
                                    <!DOCTYPE html>
                                    <html>
                                    <head>
                                      <meta charset="utf-8">
                                      <title>Print All Receipt</title>
                                      <style>
                                        * {
                                          margin: 0;
                                          padding: 0;
                                          box-sizing: border-box;
                                        }
                                        body {
                                          font-family: Arial, sans-serif;
                                          font-size: 12px;
                                          line-height: 1.4;
                                          color: black;
                                          background: white;
                                          width: 80mm;
                                          margin: 0 auto;
                                          padding: 2mm;
                                        }
                                        .print-header {
                                          text-align: center;
                                          margin-bottom: 4px;
                                          border-bottom: 2px solid black;
                                          padding-bottom: 3px;
                                        }
                                        .print-title {
                                          font-size: 16px;
                                          font-weight: bold;
                                          margin-bottom: 2px;
                                        }
                                        .print-order-info {
                                          font-size: 10px;
                                          margin-bottom: 4px;
                                        }
                                        .print-content {
                                          margin: 4px 0;
                                        }
                                        .print-item {
                                          margin-bottom: 6px;
                                          border-bottom: 1px dashed black;
                                          padding-bottom: 4px;
                                        }
                                        .print-item:last-child {
                                          border-bottom: none;
                                        }
                                        .print-item-name {
                                          font-weight: bold;
                                          font-size: 11px;
                                          margin-bottom: 2px;
                                        }
                                        .print-card {
                                          margin: 2px 0;
                                          font-size: 10px;
                                          padding: 1px 0;
                                        }
                                        .print-card-info {
                                          display: flex;
                                          justify-content: space-between;
                                          margin: 1px 0;
                                        }
                                        .print-total {
                                          text-align: center;
                                          border-top: 2px solid black;
                                          border-bottom: 2px solid black;
                                          padding: 3px 0;
                                          font-weight: bold;
                                          text-align: center;
                                          margin: 4px 0;
                                          font-size: 18px;
                                        }
                                        .print-footer {
                                          text-align: center;
                                          margin-top: 4px;
                                          border-top: 1px solid black;
                                          padding-top: 3px;
                                          font-size: 12px;
                                          line-height: 1.3;
                                        }
                                      </style>
                                    </head>
                                    <body>
                                      <div id="print-content"></div>
                                    </body>
                                    </html>
                                  `;

                                    // Set print dialog state for desktop with iframe
                                    setPrintDialog({
                                      isOpen: true,
                                      type: 'desktop',
                                      content: printHTML,
                                      orderData: {
                                        order,
                                        matchingItem: undefined, // Not used for print all
                                        card: undefined, // Not used for print all
                                        getProductTypeByProductId,
                                      },
                                    });
                                  } catch (error) {
                                    logger.error('Print all receipt failed:', error);
                                    showToast(
                                      'error',
                                      'Failed to print receipt. Please try again.',
                                    );
                                  }
                                }}
                                disabled={order.cards?.some((card) => card.scratch === true)}
                                className={`inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm ${
                                  order.cards?.some((card) => card.scratch === true)
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-purple-600 hover:bg-purple-700'
                                }`}
                              >
                                <svg
                                  className="w-4 h-4 mr-2"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                                  />
                                </svg>
                                Print All Receipt
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="border-t border-gray-200 pt-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {t('orderDetail.orderSummary') || 'Order Summary'}
                      </h2>
                      <div className="space-y-2">
                        {/* Item-wise calculation details */}
                        <div className="space-y-2 pb-3 border-b border-gray-100">
                          {order.items.map((item, index) => {
                            const itemSubtotal = item.price * item.quantity;

                            return (
                              <div
                                key={index}
                                className="flex justify-between items-center text-xs"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-gray-700 font-medium truncate">
                                    {item.name}
                                  </div>
                                  <div className="text-gray-500">
                                    {formatCurrency(
                                      item.discountPrice || item.price,
                                      item.currency,
                                    )}{' '}
                                    × {item.quantity}
                                  </div>
                                </div>
                                <div className="text-gray-900 font-medium">
                                  {formatCurrency(itemSubtotal, item.currency)}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Calculate subtotal and discount */}
                        {(() => {
                          const calculatedSubtotal =
                            order.subtotal ||
                            order.items.reduce((sum, item) => {
                              const price = item.price || item.price;
                              return sum + price * item.quantity;
                            }, 0);

                          const totalDiscount =
                            order.discountAmount ||
                            order.items.reduce((sum, item) => {
                              return sum + (item.discountAmount || 0) * item.quantity;
                            }, 0);

                          return (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  {t('orderDetail.subtotal') || 'Subtotal'}:
                                </span>
                                <span>{formatCurrency(calculatedSubtotal)}</span>
                              </div>

                              {totalDiscount > 0 && (
                                <div className="flex justify-between text-green-600">
                                  <span>{discountLabel}:</span>
                                  <span>
                                    {isMerchant ? '+' : '-'}
                                    {formatCurrency(totalDiscount)}
                                  </span>
                                </div>
                              )}

                              <div className="flex justify-between font-bold text-lg border-t border-gray-200 pt-2">
                                <span>{t('orderDetail.total') || 'Total'}:</span>
                                <span className="text-[#04BFBF]">
                                  {formatCurrency(order.totalAmount)}
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Action Buttons - Buy Again and Share */}
                      <div className="mt-6 space-y-4">
                        {/* Buy Again and Share buttons side by side */}
                        <div className="flex gap-3">
                          {/* Buy Again Button */}
                          {!order.items.some(
                            (item) => item.type === 'topup' || item.type === 'package',
                          ) && (
                            <button
                              onClick={handleBuyAgain}
                              disabled={isBuyAgainLoading}
                              className="flex-1 bg-[#04BFBF] text-white py-3 rounded-lg text-sm font-medium hover:bg-[#04BFBF]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              {isBuyAgainLoading && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              )}
                              {isBuyAgainLoading
                                ? 'Loading...'
                                : t('orderDetail-BuyAgain.buyAgain') || 'Buy Again'}
                            </button>
                          )}

                          {/* Share/Disable Share Button - Only for logged in users, non-topup/package items, and non-failed orders */}
                          {user &&
                            order.status !== 'failed' &&
                            order.status !== 'cancelled' &&
                            !order.items.some(
                              (item) => item.type === 'topup' || item.type === 'package',
                            ) &&
                            !order.cards?.some((card) => card.scratch === true) &&
                            !order.cardListPdfUrl && (
                              <button
                                onClick={
                                  order.shareInfo?.isActive
                                    ? handleDisableShare
                                    : () => setShowShareDialog(true)
                                }
                                className={`${
                                  !order.items.some(
                                    (item) => item.type === 'topup' || item.type === 'package',
                                  )
                                    ? 'flex-1'
                                    : 'w-full'
                                } ${order.shareInfo?.isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'} text-white py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2`}
                              >
                                {order.shareInfo ? (
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                                    />
                                  </svg>
                                )}
                                {order.shareInfo?.isActive
                                  ? t('orderDetail.disableSharing') || 'Disable Share'
                                  : t('orderDetail.shareAs') || 'Share as'}
                              </button>
                            )}
                        </div>

                        {/* Share Info Section - Only show when sharing is active and order is not failed */}
                        {user &&
                          order.shareInfo &&
                          order.shareInfo.isActive &&
                          order.status !== 'failed' &&
                          order.status !== 'cancelled' && (
                            <div ref={shareInfoRef} className="mt-4">
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg
                                      className="w-4 h-4 text-green-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                                      />
                                    </svg>
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-sm font-semibold text-green-900">
                                      {t('orderDetail.shareActive') || 'Sharing Active'}
                                    </h4>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {order.shareInfo.type}
                                    </span>
                                  </div>
                                </div>

                                <div className="bg-white rounded-lg border border-green-200 p-3">
                                  <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="sm:w-[80%] w-full">
                                      <p className="text-xs text-gray-600 mb-1">
                                        {t('orderDetail.shareLink') || 'Share Link'}
                                      </p>
                                      <p className="text-sm font-mono text-gray-800 break-all">
                                        {order.shareInfo.shareUrl}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => {
                                        navigator.clipboard.writeText(order.shareInfo!.shareUrl);
                                        showToast(
                                          'success',
                                          t('orderDetail.linkCopied') ||
                                            'Link copied to clipboard!',
                                        );
                                      }}
                                      className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors w-full sm:w-[20%]"
                                      title={t('orderDetail.copyLink') || 'Copy link'}
                                    >
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                        />
                                      </svg>
                                      <span>{t('orderDetail.copy') || 'Copy'}</span>
                                    </button>
                                  </div>

                                  {/* Note Section - Only show if note exists */}
                                  {(order.notes || order.shareInfo.notes) && (
                                    <div className="mt-3 pt-3 border-t border-green-200">
                                      <p className="text-xs text-gray-600 mb-1">
                                        {t('share.note') || 'Note'}
                                      </p>
                                      <p className="text-sm text-gray-800">
                                        {order.notes || order.shareInfo.notes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cart Dialog */}
      <CartDrawer isOpen={showCartDialog} onClose={() => setShowCartDialog(false)} />

      {/* Share Dialog */}
      {order && (
        <ShareDialog
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          orderId={order._id}
          orderStatus={order.status}
          onSuccess={handleShareSuccess}
        />
      )}

      {/* Iframe Dialog */}
      {iframeDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 bg-opacity-50">
          <div
            className={`bg-white shadow-xl ${
              iframeDialog.fullWidth && iframeDialog.fullHeight
                ? 'w-full h-full'
                : iframeDialog.fullWidth
                  ? 'w-full h-full md:h-[80vh]'
                  : iframeDialog.fullHeight
                    ? 'w-full max-w-7xl h-full'
                    : 'w-full max-w-7xl h-full md:h-[80vh] mx-4 md:rounded-lg'
            }`}
          >
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {iframeDialog.title}
                </h3>
                <div className="flex items-center gap-3">
                  {/* Language Toggle Button - Only show for instruction dialog and when showLanguageToggle is true */}
                  {iframeDialog.type === 'instruction' &&
                    iframeDialog.showLanguageToggle !== false && (
                      <button
                        type="button"
                        className="w-[60px] rounded-full bg-gray-50
                                        px-2.5 py-1 text-sm font-semibold 
                                        shadow-sm border border-gray-200
                                        hover:bg-gray-100 hover:cursor-pointer 
                                        transition-all duration-300
                                        hover:shadow-md active:scale-95"
                        onClick={() =>
                          handleLanguageChangeInIframe(i18n.language === 'en' ? 'mm' : 'en')
                        }
                      >
                        <span className="font-size-[11px] font-semibold text-gray-900">
                          {i18n.language === 'en' ? 'မြန်မာ' : 'ENG'}
                        </span>
                      </button>
                    )}
                  {/* Open in New Tab Button - Only show for redeem dialog */}
                  {iframeDialog.type === 'redeem' && (
                    <button
                      type="button"
                      className="rounded-full bg-gray-50
                                        px-2.5 py-1 text-sm font-semibold 
                                        shadow-sm border border-gray-200
                                        hover:bg-gray-100 hover:cursor-pointer 
                                        transition-all duration-300
                                        hover:shadow-md active:scale-95
                                        flex items-center gap-1"
                      onClick={() => window.open(iframeDialog.url, '_blank')}
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      <span className="font-size-[11px] font-semibold text-gray-900 whitespace-nowrap">
                        {t('orderDetail.openInNewTab') || 'Open'}
                      </span>
                    </button>
                  )}
                  {/* Close Button */}
                  <button
                    onClick={handleCloseIframeDialog}
                    className="text-gray-400 hover:text-gray-600 transition-colors bg-black/3 rounded-full p-1.5"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {/* Show URL for redeem dialog only */}
              {iframeDialog.type === 'redeem' && (
                <div>
                  <p className="text-xs text-gray-500 truncate">{iframeDialog.url}</p>
                </div>
              )}
            </div>
            <div className="h-[calc(100%-4rem)] rounded-b-lg relative">
              {iframeLoading && (
                <div className="absolute inset-0 bg-white rounded-b-lg flex items-center justify-center z-10">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#04BFBF]"></div>
                    <p className="text-gray-600 text-sm">{t('loading') || 'Loading...'}</p>
                  </div>
                </div>
              )}
              <iframe
                src={iframeDialog.url}
                className="w-full h-full border-0 rounded-b-lg"
                loading="lazy"
                title={iframeDialog.title}
                sandbox={getSandboxAttributes(iframeDialog.url)}
                referrerPolicy="strict-origin-when-cross-origin"
                allow="encrypted-media; fullscreen"
                onLoad={() => setIframeLoading(false)}
                onError={() => {
                  setIframeLoading(false);
                  showToast('error', t('orderDetail.iframeLoadError') || 'Failed to load content');
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Feedback Survey Section (hidden for merchants) */}
      {!isMerchant && showFeedbackSurvey && order && (
        <div className="fixed bottom-24 sm:bottom-4 right-4 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-200 z-50 md:w-96 sm:w-80 xs:w-72">
          <div className="p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">
                {/* {t('orderDetail.feedbackTitle') || 'How was your experience?'} */}
                Happiness <span style={{ fontFamily: 'Caveat, cursive' }}>Meter</span>
              </h3>
              <button
                onClick={() => {
                  setShowFeedbackSurvey(false);
                  setFeedbackIframeLoading(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 rounded-full p-1.5 flex-shrink-0"
              >
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="h-48 sm:h-64 rounded-lg overflow-hidden border border-gray-200 relative">
              {feedbackIframeLoading && (
                <div className="absolute inset-0 bg-white rounded-lg flex items-center justify-center z-10">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#04BFBF]"></div>
                    <p className="text-gray-600 text-xs">{t('loading') || 'Loading...'}</p>
                  </div>
                </div>
              )}
              {showThankYou ? (
                <div className="w-full h-full flex items-center justify-center bg-green-50 rounded-lg">
                  <div className="text-center">
                    <div className="mb-4">
                      <svg
                        className="w-12 h-12 text-green-500 mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-green-800 mb-2">
                      {t('orderDetail.thankYouFeedback') || 'Thank you for your feedback!'}
                    </h4>
                    <p className="text-sm text-green-600">
                      {t('orderDetail.feedbackSubmitted') || 'Your feedback has been submitted.'}
                    </p>
                  </div>
                </div>
              ) : (
                <iframe
                  id="feedback-survey-iframe"
                  src={`https://ezmyanmar.com/myeztopup-survey/?order_id=${order.orderNumber}&uid=${order.userId}`}
                  className="w-full h-full border-0"
                  loading="lazy"
                  title="Feedback Survey"
                  sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="encrypted-media"
                  ref={feedbackIframeRef}
                  onLoad={() => setFeedbackIframeLoading(false)}
                  onError={() => {
                    setFeedbackIframeLoading(false);
                    showToast(
                      'error',
                      t('orderDetail.iframeLoadError') || 'Failed to load content',
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Print Dialog */}
      {printDialog.isOpen
        ? createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white/95 backdrop-blur-md border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 w-full max-w-lg mx-4">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Print Receipt</h3>
                </div>

                <div className="flex-1 overflow-hidden mb-6">
                  <iframe
                    srcDoc={printDialog.content}
                    className="w-full mx-auto h-[50vh] border-0 rounded-lg"
                    title="Print Preview"
                    onLoad={(e) => {
                      const iframe = e.target as HTMLIFrameElement;
                      if (iframe.contentDocument && printDialog.orderData) {
                        const printContent = iframe.contentDocument.getElementById('print-content');

                        if (printContent) {
                          const { order, matchingItem, card, getProductTypeByProductId } =
                            printDialog.orderData;

                          import('react-dom/client').then(({ createRoot }) => {
                            const root = createRoot(printContent);

                            if (matchingItem && card) {
                              root.render(
                                createElement(
                                  'div',
                                  {},
                                  createElement(PrintReceiptItem, {
                                    orderNumber: order.orderNumber,
                                    item: matchingItem,
                                    cards: [card],
                                    createdAt: order.createdAt,
                                    getProductTypeByProductId,
                                  }),
                                ),
                              );
                            } else {
                              root.render(
                                createElement(PrintAllReceipt, {
                                  orderNumber: order.orderNumber,
                                  items: order.items,
                                  cards: order.cards || [],
                                  createdAt: order.createdAt,
                                  getProductTypeByProductId,
                                }),
                              );
                            }
                          });
                        }
                      }
                    }}
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setPrintDialog((prev) => ({ ...prev, isOpen: false }))}
                    className="flex-1 bg-gray-600 text-white py-3 rounded-xl font-medium hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const iframe = document.querySelector(
                        'iframe[title="Print Preview"]',
                      ) as HTMLIFrameElement;
                      if (iframe?.contentWindow) {
                        iframe.contentWindow.print();
                      }
                    }}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors"
                  >
                    Print
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {/* Individual Print Receipt Components for each item */}
      <style>{`
        @media screen { 
          .print-only-pre-render { display: none !important; } 
          .print-only-active { 
            display: block !important; 
            visibility: visible !important;
            position: relative !important;
          } 
        }
        @media print {
          html, body { 
            margin: 0 !important; 
            padding: 0 !important; 
            width: 100% !important; 
            height: auto !important; 
            overflow: visible !important;
          }
          body { 
            display: block !important; 
            text-align: center !important; 
            overflow: visible !important; 
          }
          .print-only-pre-render { 
            display: none !important; 
          }
          .print-only-active { 
            position: static !important; 
            width: 80mm !important; 
            max-width: 80mm !important; 
            margin: 0 auto !important; 
            float: none !important; 
          }
          @page { 
            size: 80mm auto; 
            margin: 0; 
          }
        }
      `}</style>
      {order &&
        order.items.map((item) => {
          const itemCards = order.cards?.filter((card) => card.productId === item.productId) || [];

          if (itemCards.length === 0) return null;

          return (
            <div
              key={item.productId}
              className={`print-only-pre-render print-receipt-item-${item.productId}`}
            >
              <PrintReceiptItem
                orderNumber={order.orderNumber}
                item={item}
                cards={itemCards}
                createdAt={order.createdAt}
                getProductTypeByProductId={getProductTypeByProductId}
              />
            </div>
          );
        })}
    </div>
  );
};

export default OrderDetailPage;
